/* ═══════════════════════════════════════════════════════════════════════
   CRÉAPANEL — assets/cp-3d.js (chargé par snippets/cp-decor-data.liquid)
   VUE 3D DU PLAN.
   Le modèle vient de CP_plan (cp-modele : le plan envoyé avec la commande), rien d'autre : pièces et
     leurs contours en mm, usinages de coin (type, dimensions, sommet), chants par arête, jonctions,
     épaisseur, jambages. Les découpes des usinages suivent la règle du dessin (cp-trace, cornerCutPath).
   Décor : image du décor choisi (getWorkImageUrl), motif à l'échelle en mm (texture_mm) et en miroir,
     fil tourné comme dans cp-texture (grainAngle) ; décor sans image : sa couleur. Étagère (R132) : une pièce peut
     avoir son propre décor (cp-etagere, CP_etagDecorPieces), avec son motif et son image.
   Chants : les arêtes du plan qui portent un chant sont au décor, les autres en panneau brut ; l'arc
     d'un arrondi ou d'un demi-rond et la face d'un chanfrein sont chantés comme sur le dessin.
   Lecture seule : ni l'état, ni le prix, ni le panier, ni la commande ne changent.
   Three.js r128 (cdnjs, environ 150 Ko compressés, empreinte SRI) n'est chargé qu'au premier clic ;
     sans WebGL, un message le dit et le plan 2D reste. Échap ferme ; souris, doigt et clavier ; double-clic
     (ou double appui) sur un coin : la vue s'en approche.
   : la même scène sert d'aperçu dans la fenêtre des jambages (CP_3d.legsPreview, § 5).
   ═══════════════════════════════════════════════════════════════════════*/
(function () {
  'use strict';

  var THREE_URL = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
  var THREE_SRI = 'sha384-CI3ELBVUz9XQO+97x6nwMDPosPR5XvsxW2ua7N1Xeygeh1IxtgqtCkGfQY9WWdHu';
  var THREE_KO = 150;                                    /* poids compressé annoncé (mesuré : 118 à 146 Ko) */
  var RAW = 0xcbb893, UNDER = 0xe7e2d8, SEAM = 0x3a3a3a;  /* panneau brut, dessous, trait de jonction */
  var LEG_GAP = 1.5, LEG_IN = 1;                          /*  joue 1,5 mm sous le plan, rentrée de 1 mm (mm)*/

  function getApp() { try { return (typeof App !== 'undefined') ? App : window.App; } catch (e) { return window.App; } }
  function num(v) { var n = Number(v); return isFinite(n) ? n : 0; }
  function len(a, b) { return Math.hypot(b[0] - a[0], b[1] - a[1]); }
  function unit(a, b) { var l = len(a, b) || 1; return [(b[0] - a[0]) / l, (b[1] - a[1]) / l]; }
  function add(p, u, t) { return [p[0] + u[0] * t, p[1] + u[1] * t]; }
  function cross(a, b, c) { return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]); }
  function area2(p) { return p.reduce(function (s, a, i) { var b = p[(i + 1) % p.length]; return s + a[0] * b[1] - b[0] * a[1]; }, 0); }
  function segDist(p, a, b) {
    var l2 = (b[0] - a[0]) * (b[0] - a[0]) + (b[1] - a[1]) * (b[1] - a[1]);
    var t = l2 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * (b[0] - a[0]) + (p[1] - a[1]) * (b[1] - a[1])) / l2)) : 0;
    return len(p, [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
  }
  function onBoundary(pts, p, tol) { return pts.some(function (a, i) { return segDist(p, a, pts[(i + 1) % pts.length]) <= tol; }); }
  /* largeur vraie d'une pièce convexe : plus petite « hauteur » au-dessus d'une de ses arêtes */
  function minWidth(pts) {
    var best = Infinity;
    pts.forEach(function (a, i) {
      var b = pts[(i + 1) % pts.length], L = len(a, b); if (L < 0.5) return;
      var h = 0; pts.forEach(function (q) { h = Math.max(h, Math.abs(cross(a, b, q)) / L); });
      best = Math.min(best, h);
    });
    return isFinite(best) ? best : 0;
  }
  /* R39-b : contour ({ p, arc, cut, bev }) gardé du côté s de la droite x = X (s = -1 : x ≤ X ; s = 1 : x ≥ X) ; un point posé
     sur la coupe n'a pas de marque (l'arête de coupe est une jonction, sans chant) */
  function coupeX(pts, X, s) {
    var out = [], n = pts.length;
    for (var i = 0; i < n; i++) {
      var a = pts[i], b = pts[(i + 1) % n], ia = s * (a.p[0] - X) >= -1e-9, ib = s * (b.p[0] - X) >= -1e-9;
      if (ia) out.push(a);
      if (ia !== ib) { var t = (X - a.p[0]) / (b.p[0] - a.p[0]); out.push({ p: [X, a.p[1] + (b.p[1] - a.p[1]) * t] }); }
    }
    return out;
  }

  /* ── 1. Contours usinés (repère du plan, mm) ─────────────────────────────
     Pour chaque usinage : les deux « murs » du sommet (arêtes qui ne sont pas une jonction), la découpe
     tracée entre eux comme cornerCutPath (arrondi : rayon au plus la moitié de la largeur, demi-rond :
     moitié de la largeur ; chanfrein et encoche : dx le long du mur le plus horizontal, dy de l'autre ;
     encoche décalée : le sommet reste), puis appliquée à chaque pièce du sommet (coin partagé en coupe
     à 45° : la découpe est coupée par la jonction). Chaque point garde la marque « arc » ou « coupe ». */
  function machine(plan) {
    var P = plan.pieces.map(function (p) { return { id: p.id, role: p.role, fil: num(p.fil_deg), orig: p.contour.map(function (q) { return [num(q[0]), num(q[1])]; }), pts: p.contour.map(function (q) { return { p: [num(q[0]), num(q[1])] }; }) }; });
    var U = plan.usinages || {};
    Object.keys(U).forEach(function (k) {
      var u = U[k], V = u.sommet; if (!V || !u.type || u.type === 'straight') return;
      V = [num(V[0]), num(V[1])];
      var owners = P.filter(function (p) { return p.orig.some(function (q) { return len(q, V) < 1; }); });
      if (!owners.length) return;
      var walls = [];
      owners.forEach(function (p) {
        var n = p.orig.length, i = p.orig.findIndex(function (q) { return len(q, V) < 1; });
        [p.orig[(i + n - 1) % n], p.orig[(i + 1) % n]].forEach(function (w) {
          var mid = [(V[0] + w[0]) / 2, (V[1] + w[1]) / 2];
          if (P.some(function (o) { return o !== p && onBoundary(o.orig, mid, 1); })) return;   /* jonction ou onglet */
          var d = unit(V, w);
          if (!walls.some(function (x) { return Math.abs(x.u[0] - d[0]) < 1e-6 && Math.abs(x.u[1] - d[1]) < 1e-6; })) walls.push({ u: d, L: len(V, w) });
        });
      });
      if (walls.length !== 2) return;
      var u1 = walls[0].u, u2 = walls[1].u, h1 = Math.abs(u1[0]) >= Math.abs(u1[1]), t = u.type, dim = u.dim || {};
      var dep = Math.min.apply(null, owners.map(function (p) { return minWidth(p.orig); }));
      var B = null, slot = null;
      if (t === 'bevel' || t === 'notch') {
        var dx = num(dim.dx) || 50, dy = num(dim.dy) || 50, off = t === 'notch' ? num(dim.offset) : 0, a1 = h1 ? dx : dy, a2 = h1 ? dy : dx;
        if (t === 'notch' && off > 0) {
          var uh = h1 ? u1 : u2, uv = h1 ? u2 : u1, q = function (s, tt) { return [V[0] + uh[0] * s + uv[0] * tt, V[1] + uh[1] * s + uv[1] * tt]; };
          slot = { dir: uh, pts: [q(off, 0), q(off, dy), q(off + dx, dy), q(off + dx, 0)] };
        } else if (t === 'bevel') B = [{ p: add(V, u1, a1), cut: 1, bev: k }, { p: add(V, u2, a2), cut: 1, bev: k }];
        else B = [{ p: add(V, u1, a1), cut: 1 }, { p: [V[0] + u1[0] * a1 + u2[0] * a2, V[1] + u1[1] * a1 + u2[1] * a2], cut: 1 }, { p: add(V, u2, a2), cut: 1 }];
      } else if (t === 'biais') {                                              /* hors équerre d'un bout : le coin glisse du retrait le long du bord mur_avant (avant ou arrière) */
        var xr = num(u.retrait_mm) || 50, wf = (h1 === (u.mur_avant !== 'vertical')) ? walls[0] : walls[1];
        B = [{ p: add(V, wf.u, Math.min(xr, wf.L - 1)), cut: 1, bev: k }];
      } else if (t === 'round1' || t === 'semi') {
        var r = t === 'semi' ? dep / 2 : Math.min(num(u.rayon_mm) || 30, dep / 2);
        var th = Math.acos(Math.max(-1, Math.min(1, u1[0] * u2[0] + u1[1] * u2[1]))), half = Math.tan(th / 2), tt = r / half;
        var cap = Math.min(walls[0].L, walls[1].L) * (t === 'semi' ? 1 : 0.49);
        if (tt > cap) { tt = cap; r = tt * half; }
        var bl = Math.hypot(u1[0] + u2[0], u1[1] + u2[1]) || 1, bis = [(u1[0] + u2[0]) / bl, (u1[1] + u2[1]) / bl], dc = r / Math.sin(th / 2);
        var C = [V[0] + bis[0] * dc, V[1] + bis[1] * dc], T1 = add(V, u1, tt), T2 = add(V, u2, tt);
        var g1 = Math.atan2(T1[1] - C[1], T1[0] - C[0]), g2 = Math.atan2(T2[1] - C[1], T2[0] - C[0]), dg = g2 - g1;
        while (dg > Math.PI) dg -= 2 * Math.PI; while (dg < -Math.PI) dg += 2 * Math.PI;
        B = [{ p: T1, arc: k }];
        for (var s = 1; s < 16; s++) { var g = g1 + dg * s / 16; B.push({ p: [C[0] + r * Math.cos(g), C[1] + r * Math.sin(g)], arc: k }); }
        B.push({ p: T2, arc: k });
      }
      owners.forEach(function (p) {
        var n = p.pts.length, i = p.pts.findIndex(function (o) { return len(o.p, V) < 1; }); if (i < 0) return;
        var prev = p.pts[(i + n - 1) % n].p, next = p.pts[(i + 1) % n].p;
        var isWall = function (w) { var d = unit(V, w); return walls.some(function (x) { return Math.abs(x.u[0] - d[0]) < 1e-6 && Math.abs(x.u[1] - d[1]) < 1e-6; }); };
        var wp = isWall(prev), wn = isWall(next);
        if (slot) {                                                            /* encoche décalée : dans le mur horizontal */
          var dp = unit(V, prev), dn = unit(V, next), same = function (d) { return Math.abs(d[0] - slot.dir[0]) < 1e-6 && Math.abs(d[1] - slot.dir[1]) < 1e-6; };
          var ins = slot.pts.map(function (q) { return { p: q, cut: 1 }; });
          if (same(dn)) p.pts.splice(i + 1, 0, ins[0], ins[1], ins[2], ins[3]);
          else if (same(dp)) p.pts.splice(i, 0, ins[3], ins[2], ins[1], ins[0]);
          return;
        }
        if (!B || (!wp && !wn)) return;
        var startsOn = function (seq, w) { var d = unit(V, w), a = unit(V, seq[0].p); return Math.abs(d[0] - a[0]) < 1e-3 && Math.abs(d[1] - a[1]) < 1e-3; };
        var seq;
        if (wp && wn) {
          seq = startsOn(B, prev) ? B : B.slice().reverse();
          p.pts.splice.apply(p.pts, [i, 1].concat(seq));
          return;
        }
        /* un seul mur : la découpe s'arrête à la jonction (coin partagé en coupe à 45°) */
        var wall = wp ? prev : next, J = wp ? next : prev;
        seq = startsOn(B, wall) ? B : B.slice().reverse();
        var cen = p.orig.reduce(function (a, q) { return [a[0] + q[0] / p.orig.length, a[1] + q[1] / p.orig.length]; }, [0, 0]);
        var side = Math.sign(cross(V, J, cen)), clip = [];
        for (var k2 = 0; k2 < seq.length; k2++) {
          var q0 = seq[k2].p, in0 = Math.sign(cross(V, J, q0)) * side >= -1e-9;
          if (in0) { clip.push(seq[k2]); continue; }
          if (k2 > 0) {
            var a = seq[k2 - 1].p, c0 = cross(V, J, a), c1 = cross(V, J, q0), f = c0 / ((c0 - c1) || 1);
            clip.push({ p: [a[0] + (q0[0] - a[0]) * f, a[1] + (q0[1] - a[1]) * f], arc: seq[k2].arc, cut: seq[k2].cut, bev: seq[k2].bev });
          }
          break;
        }
        if (!clip.length) return;
        p.pts.splice.apply(p.pts, [i, 1].concat(wp ? clip : clip.slice().reverse()));
      });
    });
    /* R39-b : le plan coupé de part et d'autre d'un évier monobloc (pièce entière, modélisée à part, monoGeo) ; chaque bout garde
       la pièce entière pour son image (uvPts : même placement du décor qu'en 2D, d'un bout à l'autre) */
    var MO = (plan.decoupes || []).filter(function (d) { return d.forme === 'monobloc' && d.rectangle_plan_mm; }).map(function (d) { return [num(d.rectangle_plan_mm.x), num(d.rectangle_plan_mm.x) + num(d.rectangle_plan_mm.l)]; });
    if (MO.length) P = P.reduce(function (out, p) {
      var parts = [p.pts];
      MO.forEach(function (m) { parts = parts.reduce(function (o2, pts) { return o2.concat([coupeX(pts, m[0], -1), coupeX(pts, m[1], 1)]); }, []); });
      parts.filter(function (pts) { return pts.length >= 3 && Math.abs(area2(pts.map(function (o) { return o.p; }))) > 1; })
        .forEach(function (pts) { out.push({ id: p.id, role: p.role, fil: p.fil, orig: p.orig, pts: pts, uvPts: p.pts }); });
      return out;
    }, []);
    /* chants : arête finale au décor si elle est portée par une arête à chant du plan (même pièce) ;
       arc d'un arrondi ou d'un demi-rond et face d'un chanfrein : toujours chantés, comme le trait bleu
       du dessin (drawCornerBanding, sans condition ; le moteur impose alors le chant des murs voisins) */
    var CH = (plan.chants_aretes || []).map(function (c) { return { piece: c.piece, a: [num(c.arete[0][0]), num(c.arete[0][1])], b: [num(c.arete[1][0]), num(c.arete[1][1])] }; });
    P.forEach(function (p) {
      /* point en double retiré (deux demi-ronds qui se rejoignent) : le suivant garde sa marque d'entrée */
      var src = p.pts.map(function (o) { return { p: o.p, arc: o.arc, cut: o.cut, bev: o.bev }; }), pts = [];
      src.forEach(function (o, j) { var nx = src[(j + 1) % src.length]; if (len(o.p, nx.p) > 0.05) pts.push(o); else { nx.inArc = nx.inArc || o.arc; nx.inBev = nx.inBev || o.bev; } });
      p.pts = pts;
      p.arcSeg = pts.map(function (o, j) { var nx = pts[(j + 1) % pts.length]; return !!(o.arc && (o.arc === nx.arc || o.arc === nx.inArc)); });
      p.band = pts.map(function (o, j) {
        var nx = pts[(j + 1) % pts.length], a = o.p, b = nx.p, L = len(a, b);
        if (p.arcSeg[j] || (o.bev && (o.bev === nx.bev || o.bev === nx.inBev))) return true;
        return CH.some(function (c) {
          if (c.piece !== p.id) return false;
          var Lc = len(c.a, c.b); if (Lc < 0.5) return false;
          if (Math.abs(cross(c.a, c.b, a)) / Lc > 2 || Math.abs(cross(c.a, c.b, b)) / Lc > 2) return false;
          var ux = (c.b[0] - c.a[0]) / Lc, uy = (c.b[1] - c.a[1]) / Lc, ta = (a[0] - c.a[0]) * ux + (a[1] - c.a[1]) * uy, tb = (b[0] - c.a[0]) * ux + (b[1] - c.a[1]) * uy;
          return Math.min(Math.max(ta, tb), Lc) - Math.max(Math.min(ta, tb), 0) > Math.min(1, L / 2);
        });
      });
    });
    return P;
  }

  /* jambage : panneau sous la pièce nommée, à son extrémité (plus loin de la jonction pour « bout »/« bas ») */
  function legFootprint(plan, leg) {
    /* cp-jambages nomme « plan » la pièce du plan droit, le modèle « plan principal » */
    var pc = plan.pieces.filter(function (p) { return p.role === leg.piece; })[0] ||
      plan.pieces.filter(function (p) { return String(p.role).indexOf(leg.piece) === 0; })[0];
    if (!pc) return null;
    var pts = pc.contour.map(function (q) { return [num(q[0]), num(q[1])]; }), a = num(pc.fil_deg) * Math.PI / 180, ax = [Math.cos(a), Math.sin(a)];
    var pr = pts.map(function (q) { return q[0] * ax[0] + q[1] * ax[1]; }), lo = Math.min.apply(null, pr), hi = Math.max.apply(null, pr);
    var endAt = function (v) { return pts.filter(function (q, i) { return Math.abs(pr[i] - v) < 1; }); };
    var ends = [{ v: lo, s: -1, e: endAt(lo) }, { v: hi, s: 1, e: endAt(hi) }].filter(function (x) { return x.e.length >= 2; });
    if (!ends.length) return null;
    var mid = function (e) { return [(e.e[0][0] + e.e[1][0]) / 2, (e.e[0][1] + e.e[1][1]) / 2]; }, pick;
    var ex = leg.extremite || '';
    if (/gauche/.test(ex)) pick = ends.slice().sort(function (x, y) { return mid(x)[0] - mid(y)[0]; })[0];
    else if (/droit/.test(ex)) pick = ends.slice().sort(function (x, y) { return mid(y)[0] - mid(x)[0]; })[0];
    else {
      var J = (plan.jonctions || []).map(function (j) { return [(num(j.segment[0][0]) + num(j.segment[1][0])) / 2, (num(j.segment[0][1]) + num(j.segment[1][1])) / 2]; });
      var dJ = function (e) { var m = mid(e); return J.length ? Math.min.apply(null, J.map(function (c) { return len(c, m); })) : m[1]; };
      pick = ends.slice().sort(function (x, y) { return dJ(y) - dJ(x); })[0];
    }
    var t = num(leg.epaisseur_mm) || num(plan.epaisseur_mm && plan.epaisseur_mm.valeur) || 20, n = [ax[0] * pick.s, ax[1] * pick.s];
    var r = Math.max(0, num(leg.retrait_mm));                                  /*  reculé de son retrait depuis le bout*/
    var E1 = [pick.e[0][0] - n[0] * r, pick.e[0][1] - n[1] * r], E2 = [pick.e[1][0] - n[0] * r, pick.e[1][1] - n[1] * r];
    /* R36-4 (AUDIT, Q5) : jambage « sans coupe » moins profond que le plan (HPL 40 : 600 sous un plan de 630) : il affleure l'avant, le
       retrait est côté mur. Côté mur du bout : pièce le long de x (plan, retour de G), le plus petit y (origine du modèle à l'arrière) ;
       pièce le long de y (retour de L, jambes de U), le côté extérieur de l'encombrement. */
    var rm = num(leg.profondeur_plan_mm) - num(leg.profondeur_mm);
    if (leg.profondeur_plan_mm && rm > 0 && len(E1, E2) > rm) {
      var mur1;
      if (Math.abs(ax[0]) >= Math.abs(ax[1])) mur1 = E1[1] <= E2[1];
      else {
        var xs = []; plan.pieces.forEach(function (p) { p.contour.forEach(function (q) { xs.push(num(q[0])); }); });
        var cx = 0; pts.forEach(function (q) { cx += q[0] / pts.length; });
        mur1 = cx <= (Math.min.apply(null, xs) + Math.max.apply(null, xs)) / 2 ? E1[0] <= E2[0] : E1[0] >= E2[0];
      }
      var Ew = mur1 ? E1 : E2, Ef = mur1 ? E2 : E1, dl = len(Ew, Ef), Em = [Ew[0] + (Ef[0] - Ew[0]) * rm / dl, Ew[1] + (Ef[1] - Ew[1]) * rm / dl];
      if (mur1) E1 = Em; else E2 = Em;
    }
    return { pts: [E1, E2, [E2[0] - n[0] * t, E2[1] - n[1] * t], [E1[0] - n[0] * t, E1[1] - n[1] * t]], h: num(leg.hauteur_mm) || 720, fil: (num(pc.fil_deg) + 90) % 180 };
  }

  /* ── 2. Chargement de Three.js (au clic, une fois) ── */
  var threeP = null;
  function loadThree() {
    if (window.THREE && window.THREE.WebGLRenderer) return Promise.resolve(window.THREE);
    if (threeP) return threeP;
    threeP = new Promise(function (res, rej) {
      var s = document.createElement('script');
      s.src = THREE_URL; s.integrity = THREE_SRI; s.crossOrigin = 'anonymous'; s.referrerPolicy = 'no-referrer'; s.async = true;
      s.onload = function () { window.THREE ? res(window.THREE) : rej(new Error('Three.js absent')); };
      s.onerror = function () { threeP = null; rej(new Error('chargement impossible')); };
      document.head.appendChild(s);
    });
    return threeP;
  }
  var glOk = null;
  function hasWebGL() {
    if (glOk !== null) return glOk;
    try { var c = document.createElement('canvas'); glOk = !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl') || c.getContext('experimental-webgl'))); }
    catch (e) { glOk = false; }
    return glOk;
  }

  /* ── 3. Scène ── */
  function slab(T, pts, band, y0, y1, uvTop, uvSide, arcSeg, trous) {
    /* dessus / dessous triangulés ; côtés un quadrilatère par arête ; groupes : 0 dessus, 1 côtés au décor,
       2 côtés bruts, 3 dessous. Côtés : décor continu le long du contour ; arcs lissés (normale du cercle
       à chaque point, celle du mur au point de tangence), murs et chanfreins à arêtes vives. R200 : trous
       ({ pts, g } : contour du trou, groupe de sa paroi), percés dans le dessus et le dessous, leur paroi
       tournée vers l'intérieur du trou */
    trous = trous || [];
    var pos = [], uv = [], nor = [], groups = [[], [], [], []], v2 = pts.map(function (q) { return new T.Vector2(q[0], q[1]); });
    var tous = pts.concat.apply(pts.slice(), trous.map(function (h) { return h.pts; }));   /* indices des faces : contour puis trous */
    var tri = T.ShapeUtils.triangulateShape(v2, trous.map(function (h) { return h.pts.map(function (q) { return new T.Vector2(q[0], q[1]); }); }));
    var push = function (g, a, b, c, want) {                                /* face orientée vers « want » */
      var n = [(b[1] - a[1]) * (c[2] - a[2]) - (b[2] - a[2]) * (c[1] - a[1]), (b[2] - a[2]) * (c[0] - a[0]) - (b[0] - a[0]) * (c[2] - a[2]), (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0])];
      if (n[0] * want[0] + n[1] * want[1] + n[2] * want[2] < 0) { var t0 = b; b = c; c = t0; }
      groups[g].push(a, b, c);
    };
    tri.forEach(function (f) {
      var P3 = f.map(function (i) { return [tous[i][0], y1, tous[i][1], uvTop(tous[i]), [0, 1, 0]]; });
      push(0, P3[0], P3[1], P3[2], [0, 1, 0]);
      var Q3 = f.map(function (i) { return [tous[i][0], y0, tous[i][1], uvTop(tous[i]), [0, -1, 0]]; });
      push(3, Q3[0], Q3[1], Q3[2], [0, -1, 0]);
    });
    trous.forEach(function (h) {                                           /* R200 : paroi de chaque trou, vers son centre */
      var hp = h.pts, m = hp.length, cx = 0, cz = 0, acc2 = 0;
      hp.forEach(function (q) { cx += q[0] / m; cz += q[1] / m; });
      hp.forEach(function (a, j) {
        var b = hp[(j + 1) % m], L = len(a, b); if (L < 0.05) return;
        var mx = (a[0] + b[0]) / 2, mz = (a[1] + b[1]) / 2, dl = Math.hypot(cx - mx, cz - mz) || 1, nn = [(cx - mx) / dl, 0, (cz - mz) / dl];
        var q = function (p, y, s) { return [p[0], y, p[1], uvSide(s, y - y0), nn]; };
        var A0 = q(a, y0, acc2), B0 = q(b, y0, acc2 + L), B1 = q(b, y1, acc2 + L), A1 = q(a, y1, acc2);
        push(h.g, A0, B0, B1, nn); push(h.g, A0, B1, A1, nn);
        acc2 += L;
      });
    });
    var cw = area2(pts) > 0 ? 1 : -1, n = pts.length, arc = arcSeg || [];
    var outs = pts.map(function (a, j) { var b = pts[(j + 1) % n], L = len(a, b) || 1; return [(b[1] - a[1]) * cw / L, 0, -(b[0] - a[0]) * cw / L]; });
    var at = function (j, k) {                                               /* normale du côté j au sommet qu'il partage avec k */
      var a = outs[j], b = outs[k], d = a[0] * b[0] + a[2] * b[2];
      if (!arc[j]) return a;
      if (arc[k] && d > 0.9) { var s = Math.hypot(a[0] + b[0], a[2] + b[2]) || 1; return [(a[0] + b[0]) / s, 0, (a[2] + b[2]) / s]; }
      if (!arc[k] && d > 0.98) return b;
      return a;
    };
    var acc = 0;
    pts.forEach(function (a, j) {
      var b = pts[(j + 1) % n], L = len(a, b); if (L < 0.05) return;
      var na = at(j, (j + n - 1) % n), nb = at(j, (j + 1) % n);
      var q = function (p, y, s, nn) { return [p[0], y, p[1], uvSide(s, y - y0), nn]; };
      var A0 = q(a, y0, acc, na), B0 = q(b, y0, acc + L, nb), B1 = q(b, y1, acc + L, nb), A1 = q(a, y1, acc, na), g = band[j] ? 1 : 2;
      push(g, A0, B0, B1, outs[j]); push(g, A0, B1, A1, outs[j]);
      acc += L;
    });
    var geo = new T.BufferGeometry(), start = 0;
    groups.forEach(function (list, g) {
      list.forEach(function (v) { pos.push(v[0], v[1], v[2]); uv.push(v[3][0], v[3][1]); nor.push(v[4][0], v[4][1], v[4][2]); });
      if (list.length) geo.addGroup(start, list.length, g);
      start += list.length;
    });
    geo.setAttribute('position', new T.Float32BufferAttribute(pos, 3));
    geo.setAttribute('uv', new T.Float32BufferAttribute(uv, 2));
    geo.setAttribute('normal', new T.Float32BufferAttribute(nor, 3));
    return geo;
  }
  /* R39-b : évier monobloc, d'après les 16 faces du volume du DXF (géométrie g de cp-decoupes, MONO_GEO : u le long de sa largeur,
     v depuis le mur, h sous le dessus) : dessus percé de la cuve et du trou de robinet ; cuve creusée (parois, fond), jamais
     traversante ; bonde à 2 étages, seul trou du fond ; trou de robinet de part en part ; joues au profil du DXF (bandeau avant,
     face inclinée, base) ; dessous percé de la bonde et du robinet. Dessus du monobloc au niveau du dessus du plan (AUDIT Tour
     1175, Q2), posé à x0 dans le repère du plan. Groupes : 0 faces vues de dessus (dessus, fond de cuve, palier de bonde), 1 les
     autres, au décor comme les chants */
  function monoGeo(T, g, x0, TH, uvTop, uvSide) {
    var pos = [], uv = [], nor = [], groups = [[], []];
    var V = function (u, v, h, t, n) { return [x0 + u, TH - h, v, t, n]; };
    var push = function (gr, a, b, c, want) {
      var n = [(b[1] - a[1]) * (c[2] - a[2]) - (b[2] - a[2]) * (c[1] - a[1]), (b[2] - a[2]) * (c[0] - a[0]) - (b[0] - a[0]) * (c[2] - a[2]), (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0])];
      if (n[0] * want[0] + n[1] * want[1] + n[2] * want[2] < 0) { var t0 = b; b = c; c = t0; }
      groups[gr].push(a, b, c);
    };
    var cercle = function (u, v, r) { var o = []; for (var i = 0; i < 32; i++) { var a = i / 32 * 2 * Math.PI; o.push([u + Math.cos(a) * r, v + Math.sin(a) * r]); } return o; };
    var plat = function (gr, contour, trous, h, haut) {                    /* face horizontale à h, percée */
      var all = contour.concat.apply(contour.slice(), trous), n = [0, haut ? 1 : -1, 0];
      var v2 = function (l) { return l.map(function (q) { return new T.Vector2(q[0], q[1]); }); };
      T.ShapeUtils.triangulateShape(v2(contour), trous.map(v2)).forEach(function (f) {
        var P3 = f.map(function (i) { var q = all[i]; return V(q[0], q[1], h, uvTop([x0 + q[0], q[1]]), n); }); push(gr, P3[0], P3[1], P3[2], n);
      });
    };
    var mur = function (a, b, h0, h1, n, s0) {                              /* paroi verticale de a à b (u, v), de h0 à h1 */
      var L = Math.hypot(b[0] - a[0], b[1] - a[1]);
      var A0 = V(a[0], a[1], h0, uvSide(s0, h0), n), B0 = V(b[0], b[1], h0, uvSide(s0 + L, h0), n), B1 = V(b[0], b[1], h1, uvSide(s0 + L, h1), n), A1 = V(a[0], a[1], h1, uvSide(s0, h1), n);
      push(1, A0, B0, B1, n); push(1, A0, B1, A1, n); return L;
    };
    var creux = function (pts, h0, h1) {                                    /* paroi d'un creux, tournée vers son centre */
      var c = pts.reduce(function (s, q) { return [s[0] + q[0] / pts.length, s[1] + q[1] / pts.length]; }, [0, 0]), s = 0;
      pts.forEach(function (a, i) {
        var b = pts[(i + 1) % pts.length], m = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2], d = Math.hypot(c[0] - m[0], c[1] - m[1]) || 1;
        s += mur(a, b, h0, h1, [(c[0] - m[0]) / d, 0, (c[1] - m[1]) / d], s);
      });
    };
    var cu = g.cuve, bo = g.bonde, et = bo.etages, rb = g.robinet;
    var cuve = [[cu.u, cu.v], [cu.u + cu.l, cu.v], [cu.u + cu.l, cu.v + cu.p], [cu.u, cu.v + cu.p]], rob = cercle(rb.u, rb.v, rb.r);
    plat(0, [[0, 0], [g.l, 0], [g.l, g.p], [0, g.p]], [cuve, rob], 0, true);   /* dessus */
    creux(cuve, 0, cu.h);                                                    /* parois de la cuve */
    plat(0, cuve, [cercle(bo.u, bo.v, et[0].r)], cu.h, true);               /* fond de la cuve */
    et.forEach(function (e, i) {                                             /* bonde : chaque étage, puis son palier */
      creux(cercle(bo.u, bo.v, e.r), e.h0, e.h1);
      if (et[i + 1]) plat(0, cercle(bo.u, bo.v, e.r), [cercle(bo.u, bo.v, et[i + 1].r)], e.h1, true);
    });
    creux(rob, rb.h0, rb.h1);                                                /* trou de robinet */
    var pf = g.profil, bas = pf.filter(function (q) { return q[1] === g.h; }).map(function (q) { return q[0]; });
    plat(1, [[0, Math.min.apply(null, bas)], [g.l, Math.min.apply(null, bas)], [g.l, Math.max.apply(null, bas)], [0, Math.max.apply(null, bas)]],
      [cercle(bo.u, bo.v, et[et.length - 1].r), rob], g.h, false);         /* dessous */
    var cg = pf.reduce(function (s, q) { return [s[0] + q[0] / pf.length, s[1] + q[1] / pf.length]; }, [0, 0]);
    pf.forEach(function (a, i) {                                             /* fond, bandeau avant, face inclinée : d'une joue à l'autre */
      var b = pf[(i + 1) % pf.length]; if ((a[1] === 0 && b[1] === 0) || (a[1] === g.h && b[1] === g.h)) return;
      var e = [b[0] - a[0], b[1] - a[1]], nv = e[1], nh = -e[0], m = [(a[0] + b[0]) / 2 - cg[0], (a[1] + b[1]) / 2 - cg[1]];
      if (nv * m[0] + nh * m[1] < 0) { nv = -nv; nh = -nh; }
      var l = Math.hypot(nv, nh) || 1, n = [0, -nh / l, nv / l];
      var A0 = V(0, a[0], a[1], uvSide(0, a[1]), n), B0 = V(g.l, a[0], a[1], uvSide(g.l, a[1]), n), B1 = V(g.l, b[0], b[1], uvSide(g.l, b[1]), n), A1 = V(0, b[0], b[1], uvSide(0, b[1]), n);
      push(1, A0, B0, B1, n); push(1, A0, B1, A1, n);
    });
    [[0, -1], [g.l, 1]].forEach(function (j) {                               /* joues, au profil du DXF */
      var n = [j[1], 0, 0];
      T.ShapeUtils.triangulateShape(pf.map(function (q) { return new T.Vector2(q[0], q[1]); }), []).forEach(function (f) {
        var P3 = f.map(function (i) { return V(j[0], pf[i][0], pf[i][1], uvSide(pf[i][0], pf[i][1]), n); }); push(1, P3[0], P3[1], P3[2], n);
      });
    });
    var geo = new T.BufferGeometry(), start = 0;
    groups.forEach(function (list, gi) {
      list.forEach(function (v) { pos.push(v[0], v[1], v[2]); uv.push(v[3][0], v[3][1]); nor.push(v[4][0], v[4][1], v[4][2]); });
      if (list.length) geo.addGroup(start, list.length, gi);
      start += list.length;
    });
    geo.setAttribute('position', new T.Float32BufferAttribute(pos, 3));
    geo.setAttribute('uv', new T.Float32BufferAttribute(uv, 2));
    geo.setAttribute('normal', new T.Float32BufferAttribute(nor, 3));
    return geo;
  }
  /* évier intégré Dekton : fond en 4 pans (triangles d'un côté de la cuve au centre), côtés à yHaut, centre à yBas ; faces vers le
     haut, au décor (groupe 0) */
  function pansGeo(T, q, c, yHaut, yBas, uvTop) {
    var pos = [], uv = [], nor = [];
    q.forEach(function (a, i) {
      var b = q[(i + 1) % q.length], P3 = [[a[0], yHaut, a[1]], [b[0], yHaut, b[1]], [c[0], yBas, c[1]]];
      var u = [P3[1][0] - P3[0][0], P3[1][1] - P3[0][1], P3[1][2] - P3[0][2]], v = [P3[2][0] - P3[0][0], P3[2][1] - P3[0][1], P3[2][2] - P3[0][2]];
      var n = [u[1] * v[2] - u[2] * v[1], u[2] * v[0] - u[0] * v[2], u[0] * v[1] - u[1] * v[0]];
      if (n[1] < 0) { P3 = [P3[0], P3[2], P3[1]]; n = [-n[0], -n[1], -n[2]]; }
      var l = Math.hypot(n[0], n[1], n[2]) || 1;
      P3.forEach(function (p) { pos.push(p[0], p[1], p[2]); var t = uvTop([p[0], p[2]]); uv.push(t[0], t[1]); nor.push(n[0] / l, n[1] / l, n[2] / l); });
    });
    var geo = new T.BufferGeometry();
    geo.setAttribute('position', new T.Float32BufferAttribute(pos, 3));
    geo.setAttribute('uv', new T.Float32BufferAttribute(uv, 2));
    geo.setAttribute('normal', new T.Float32BufferAttribute(nor, 3));
    geo.addGroup(0, pos.length / 3, 0);
    return geo;
  }
  /* R39-b : hauteurs (y) où une verticale en (x, z) traverse la géométrie, de haut en bas : relevé de la cuve (non traversante),
     de la bonde et du trou de robinet (CP_3d.info().monobloc) */
  function verticale(geo, x, z) {
    var p = geo.getAttribute('position').array, ys = [];
    for (var i = 0; i < p.length; i += 9) {
      var ax = p[i], az = p[i + 2], bx = p[i + 3], bz = p[i + 5], cx = p[i + 6], cz = p[i + 8];
      var d = (bz - cz) * (ax - cx) + (cx - bx) * (az - cz); if (Math.abs(d) < 1e-9) continue;
      var l1 = ((bz - cz) * (x - cx) + (cx - bx) * (z - cz)) / d, l2 = ((cz - az) * (x - cx) + (ax - cx) * (z - cz)) / d, l3 = 1 - l1 - l2;
      if (l1 < -1e-6 || l2 < -1e-6 || l3 < -1e-6) continue;
      ys.push(Math.round((l1 * p[i + 1] + l2 * p[i + 4] + l3 * p[i + 7]) * 10) / 10);
    }
    return ys.filter(function (y, k) { return ys.indexOf(y) === k; }).sort(function (a, b) { return b - a; });
  }

  /* Lumières, les mêmes pour la vue 3D, la vignette et l'image : ciel et sol, soleil avec ombres, appoint, et une
     « fenêtre » au-dessus de l'arrière (voir placeFenetre). Dosées pour qu'un blanc ne sature pas. Pas de fenêtre
     pour l'étagère (mur derrière). */
  function lumieres(T, scene) {
    scene.add(new T.HemisphereLight(0xffffff, 0x8f887c, 0.3));
    var sun = new T.DirectionalLight(0xffffff, 0.48); sun.castShadow = true; sun.shadow.mapSize.set(1024, 1024); sun.shadow.bias = -0.0005; sun.shadow.radius = 4;
    var fill = new T.DirectionalLight(0xfff6e8, 0.16), win = new T.PointLight(0xfffaf2, 0.6, 1, 1);
    scene.add(sun); scene.add(sun.target); scene.add(fill); scene.add(fill.target); scene.add(win);
    return { sun: sun, fill: fill, win: win };
  }
  /* « fenêtre » au-dessus de l'arrière, atténuée avec la distance : sur un décor mat, le dessus va du plus clair (fond)
     au plus sombre (avant), sans tache de reflet ; elle n'éclaire pas le chant avant, qui se détache */
  function placeFenetre(win, c, R, mur) { win.position.set(c.x - R * 0.2, c.y + R * 1.0, c.z - R * 1.1); win.distance = R * 2.8; win.intensity = mur ? 0 : 0.6; }
  /* R132 : images des décors propres aux pièces de l'étagère, chargées une fois (par adresse) ; à l'arrivée, la vue est
     refaite. texPieces : promesse tenue quand toutes sont là (ou en échec), pour l'image jointe au panier */
  var TEXP = {};
  function texPiece(T, url) {
    if (!url) return null;
    var e = TEXP[url]; if (e) return e.tx;
    e = TEXP[url] = { tx: null, p: null };
    e.p = new Promise(function (fin) {
      var ld = new T.TextureLoader(); ld.setCrossOrigin('anonymous');
      ld.load(url, function (tx) {
        tx.wrapS = tx.wrapT = T.MirroredRepeatWrapping; tx.flipY = false; tx.encoding = T.sRGBEncoding;
        tx.anisotropy = Math.min(8, view && view.renderer.capabilities.getMaxAnisotropy ? view.renderer.capabilities.getMaxAnisotropy() : 1);
        e.tx = tx; fin();
        if (view && view.rebuild) view.rebuild(view.plan);
      }, undefined, function () { fin(); });
    });
    return null;
  }
  function texPieces(T) {
    var pr = typeof window.CP_etagDecorPieces === 'function' ? window.CP_etagDecorPieces() : null, us = [];
    if (pr) Object.keys(pr).forEach(function (id) { us.push(pr[id].url); });
    /* boiserie : les panneaux aussi (CP_boisDecors), sauf les unis (aplat) ; sans la 3D ouverte avant l'ajout, leur image
       n'était pas chargée et celle du panier (_preview_3d_url) montrait un aplat brun */
    var bd = typeof window.CP_boisDecors === 'function' ? window.CP_boisDecors() : null;
    if (bd) Object.keys(bd).forEach(function (id) { var dv = bd[id]; if (dv && !(dv.hex && !dv.tex)) us.push(dv.url); });
    us = us.filter(function (u, k) { return u && us.indexOf(u) === k; });
    if (!us.length) return Promise.resolve();
    return Promise.all(us.map(function (u) { if (!TEXP[u]) texPiece(T, u); return TEXP[u] ? TEXP[u].p : null; }));
  }
  function buildScene(T, plan, decorTex) {
    var d = (typeof window.CP_decorFull === 'function') ? window.CP_decorFull() : null, tex = d && d.tex;
    /*  uni (aplat_hex sans texture_mm : Blanc, Blanc lune) : sa couleur, comme le dessin (cp-trace pose
       l'aplat avant l'image), que la variante ait une image (carré uni) ou non (image du produit en repli).*/
    if (d && d.hex && !tex) decorTex = null;
    var TH = num(plan.epaisseur_mm && plan.epaisseur_mm.valeur) || 20;
    /*  + : UNE image par pièce, calculée comme en 2D (CP_texture.fit : étirée dans le sens du fil, × 2,5 au
       plus, sinon un raccord en miroir au milieu ; marbre, pierre, uni : image agrandie et recadrée). Les coordonnées
       de texture suivent la boîte de la pièce dans le repère du fil ; au-delà de 1, la répétition en miroir de la carte
       graphique donne le raccord du milieu.*/
    var FIT = window.CP_texture && window.CP_texture.fit, BOX = window.CP_texture && window.CP_texture.boxIn;
    /* uv d'un point (X, Y) de la boîte (repère du fil, origine au coin de la boîte) pour le placement F */
    var uvFit = function (F, X, Y) {
      if (F.rot90) return [((Y - F.cy) + F.w / 2) / F.w, (-(X - F.cx) + F.h / 2) / F.h];
      return [(X - F.x) / F.w, (Y - F.y) / F.h];
    };
    var col = (d && d.hex) || null;
    if (!col) { var A = getApp(), m = A && A.variantsMeta && A.variantsMeta[A.S && A.S.variant]; col = (m && m.fallbackColor) || '#d9d4cb'; }
    /* aplat : couleur sRGB (hex) ; la vue sort en sRGB (outputEncoding) : la couleur est passée en linéaire, sinon elle
       ressort éclaircie et délavée (#813c3d affiché vers #ba8485). Les images, elles, sont déjà déclarées en sRGB. */
    var lin = function (h) { var c = new T.Color(h); return c.convertSRGBToLinear ? c.convertSRGBToLinear() : c; };
    /* R132 : l'habillage d'un décor (motif texture_mm, image chargée, aplat) : celui de la fiche, et, pour l'étagère, celui
       d'une pièce qui a son propre décor (cp-etagere, CP_etagDecorPieces) */
    var habillage = function (tex, decorTex, col) {
      var tl = tex ? num(tex.l) : 1000, tht = tex ? num(tex.h) : 1000, fil = tex ? tex.fil : 'x';
      var rotOf = function (filDeg) { return (fil === 'y' ? filDeg - 90 : fil === 'x' ? filDeg : 0) * Math.PI / 180; };   /* cp-texture, grainAngle */
      /* décor sans texture_mm (crédence, panneaux : le dessin du moteur y pose déjà UNE image par pièce) : même règle,
         avec les proportions de l'image chargée, sans fil (image agrandie et recadrée). Avant : motif de 1 000 mm répété
         en miroir, d'où les « 3 blocs » sur la crédence de 2 400 mm.*/
      var ftex = tex || (decorTex && decorTex.image && decorTex.image.width > 0 ? { l: decorTex.image.width, h: decorTex.image.height, fil: 'iso' } : null);
      if (decorTex && (decorTex.wrapS !== T.MirroredRepeatWrapping || decorTex.wrapT !== T.MirroredRepeatWrapping)) {
        decorTex.wrapS = decorTex.wrapT = T.MirroredRepeatWrapping; decorTex.needsUpdate = true;
      }
      /* mat complet (rugosité 1) : pas de reflet qui délaverait la teinte ou saturerait un blanc ; le relief vient des lumières */
      var decorMat = new T.MeshStandardMaterial(decorTex ? { map: decorTex, roughness: 1, metalness: 0 } : { color: lin(col), roughness: 1, metalness: 0 });
      return { tex: tex, decorTex: decorTex, col: col, tl: tl, tht: tht, fil: fil, rotOf: rotOf, ftex: ftex, decorMat: decorMat, edgeMat: null };
    };
    /*  chants du plan un peu plus sombres que le dessus : l'épaisseur se lit comme une arête, et le plan se
       détache de la joue posée dessous. Créés après les matières des arêtes et du panneau brut, comme avant R132 :
       l'ordre de création des matières fixe l'ordre de dessin (image identique à l'octet pour les plans) */
    var chants = function (H) { H.edgeMat = H.decorMat.clone(); H.edgeMat.color = H.decorTex ? new T.Color(0xe4e4e4) : lin(H.col).multiplyScalar(0.8); return H; };
    var H0 = habillage(tex, decorTex, col), tl = H0.tl, tht = H0.tht, fil = H0.fil, rotOf = H0.rotOf, ftex = H0.ftex, decorMat = H0.decorMat;
    /* arêtes vives du plan (dessus, chants, coins) soulignées d'un trait sombre léger : l'épaisseur se lit même de loin */
    var areteMat = new T.LineBasicMaterial({ color: 0x1f1d1a, transparent: true, opacity: 0.3 });
    var rawMat = new T.MeshStandardMaterial({ color: RAW, roughness: 0.95 }), underMat = new T.MeshStandardMaterial({ color: UNDER, roughness: 0.95 });
    var edgeMat = chants(H0).edgeMat;
    var mats = [decorMat, edgeMat, rawMat, underMat], group = new T.Group();
    /* R200 (Romain : « on doit voir les decoupes sur la 3d » ; AUDIT Tours 1074 et 1085) : chaque découpe du plan (plan.decoupes,
       cp-decoupes) est percée dans la pièce qui la contient entière : rectangle aux coins arrondis (rayon_mm), rond, trou pour
       prise à sa taille (R201), trou de robinet pris (robinet_plan_mm). Paroi au décor sur la pierre (comme un chant), brute
       ailleurs (l'âme). Un trou qui sort de sa pièce ou en chevauche un autre n'est pas percé ; un trou d'avant R201 (sans
       taille) non plus. Le dessin, les prix et le panier ne changent pas */
    var PIERRE = /quartz|silestone|neolith|dekton|gres|pierre/i.test((plan.produit && plan.produit.handle) || '');
    var dedans = function (poly, q) {
      var c = false;
      for (var i = 0, j = poly.length - 1; i < poly.length; j = i++) { var a = poly[i], b = poly[j]; if ((a[1] > q[1]) !== (b[1] > q[1]) && q[0] < (b[0] - a[0]) * (q[1] - a[1]) / (b[1] - a[1]) + a[0]) c = !c; }
      return c;
    };
    var formes = (plan.decoupes || []).reduce(function (out, d) {
      var r = d.rectangle_plan_mm; if (!r || !(num(r.l) > 0) || !(num(r.p) > 0) || d.forme === 'monobloc') return out;   /* R39-b : le monobloc n'est pas percé, il remplace le plan */
      var x = num(r.x), y = num(r.y), l = num(r.l), pp = num(r.p), pts = [];
      if (d.forme === 'rond') { for (var k = 0; k < 48; k++) { var a = k / 48 * 2 * Math.PI; pts.push([x + l / 2 + Math.cos(a) * l / 2, y + pp / 2 + Math.sin(a) * pp / 2]); } }
      else {
        var rr = Math.max(0, Math.min(num(d.rayon_mm), l / 2 - 0.5, pp / 2 - 0.5));
        if (rr < 0.5) pts = [[x, y], [x + l, y], [x + l, y + pp], [x, y + pp]];
        else [[x + l - rr, y + rr, -90], [x + l - rr, y + pp - rr, 0], [x + rr, y + pp - rr, 90], [x + rr, y + rr, 180]].forEach(function (c) {
          for (var s = 0; s <= 8; s++) { var a3 = (c[2] + s * 90 / 8) * Math.PI / 180; pts.push([c[0] + Math.cos(a3) * rr, c[1] + Math.sin(a3) * rr]); }
        });
      }
      if (d.cuve && num(d.cuve.profondeur_mm) > 0) pts.cuve = d.cuve;   /* R39-c : évier intégré, la cuve sous la découpe */
      out.push(pts);
      var rb = d.robinet_plan_mm;
      if (rb && num(rb.r) > 0) { var rob = []; for (var k2 = 0; k2 < 32; k2++) { var a2 = k2 / 32 * 2 * Math.PI; rob.push([num(rb.cx) + Math.cos(a2) * num(rb.r), num(rb.cy) + Math.sin(a2) * num(rb.r)]); } out.push(rob); }
      return out;
    }, []);
    var cuves = [];
    var trousDe = function (p) {
      var poly = p.pts.map(function (o) { return o.p; }), pris = [];
      formes.forEach(function (h) {
        if (!h.every(function (q) { return dedans(poly, q); })) return;                                  /* entier dans la pièce */
        if (pris.some(function (o) { return o.some(function (q) { return dedans(h, q); }) || h.some(function (q) { return dedans(o, q); }); })) return;   /* sur aucun autre */
        pris.push(h); if (h.cuve) cuves.push(h);                         /* R39-c : la cuve n'est creusée que sous un trou percé */
      });
      return pris.map(function (h) { return { pts: h, g: PIERRE ? 1 : 2 }; });
    };
    /* coordonnées de texture et matières d'une pièce ; R39-b : un bout de plan coupé par le monobloc garde celles de la pièce
       entière (uvPts), et le monobloc prend celles du plan qu'il coupe : le décor court d'un bout à l'autre, comme en 2D */
    var habille = function (p, H) {
      var r = H.rotOf(p.fil), c = Math.cos(r), s = Math.sin(r), o = p.orig[0];
      var uvTop = function (q) { var x = q[0] - o[0], y = q[1] - o[1]; return [(x * c + y * s) / H.tl, (-x * s + y * c) / H.tht]; };
      var uvSide = function (sAlong, h) { return H.fil === 'y' ? [h / H.tl, sAlong / H.tht] : [sAlong / H.tl, h / H.tht]; };
      var pmats = H === H0 ? mats : [H.decorMat, H.edgeMat, rawMat, underMat];
      if (H.ftex && FIT && BOX) {
        var bx = BOX((p.uvPts || p.pts).map(function (o2) { return o2.p; }), o[0], o[1], r * 180 / Math.PI), F = FIT(H.ftex, bx.w, bx.h), ftx = H.ftex;
        /* rendu c des bois (cp-texture : décors à fil) : image de la pièce fabriquée à l'échelle, raccords fondus, même
           boîte et même tirage qu'en 2D ; minéraux (iso) : image unique agrandie, comme avant */
        var TM = window.CP_texture, md = TM && TM.mode ? TM.mode(H.tex) : 'a';
        if (md === 'c' && H.tex && H.decorTex && H.decorTex.image && TM.synthCanvas) {
          var cv = null; try { cv = TM.synthCanvas(H.decorTex.image, H.tex, bx.w, bx.h, 'c|' + Math.round(bx.w) + 'x' + Math.round(bx.h), 4096); } catch (e2) { cv = null; }
          if (cv) {
            var ct = new T.CanvasTexture(cv); ct.flipY = false; ct.encoding = T.sRGBEncoding; ct.wrapS = ct.wrapT = T.ClampToEdgeWrapping;
            ct.anisotropy = H.decorTex.anisotropy || 1;
            var pm = H.decorMat.clone(), em = H.edgeMat.clone(); pm.map = ct; em.map = ct;
            pmats = [pm, em, rawMat, underMat];
            ftx = { l: bx.w, h: bx.h, fil: 'iso' }; F = FIT(ftx, bx.w, bx.h);
          }
        }
        uvTop = function (q) { var x = q[0] - o[0], y = q[1] - o[1]; return uvFit(F, x * c + y * s - bx.x0, -x * s + y * c - bx.y0); };
        /* chants : même échelle que le dessus, le long de l'arête et dans l'épaisseur */
        uvSide = function (sAlong, h) { return ftx.fil === 'y' ? [h / F.w, sAlong / F.h] : [sAlong / F.w, h / F.h]; };
      }
      return { uvTop: uvTop, uvSide: uvSide, mats: pmats };
    };
    var pieceMesh = function (p, H) {
      var hb = habille(p, H), tr = trousDe(p), mesh = new T.Mesh(slab(T, p.pts.map(function (o2) { return o2.p; }), p.band, 0, TH, hb.uvTop, hb.uvSide, p.arcSeg, tr), hb.mats);   /* R200 : ses trous */
      mesh.userData.trous = tr.length;
      mesh.castShadow = true; mesh.receiveShadow = true; mesh.userData.piece = p.id;
      mesh.add(new T.LineSegments(new T.EdgesGeometry(mesh.geometry, 25), areteMat));   /* 25° : pas de traits dans les arcs */
      return mesh;
    };
    /* R132 : étagère, pièce au décor propre : son motif et son image (chargée une fois, texPiece ; aplat en attendant) */
    var propres = /etagere|boiserie/i.test((plan.produit && plan.produit.handle) || '') && typeof window.CP_etagDecorPieces === 'function' ? window.CP_etagDecorPieces() : null, Hs = {};
    var habillageDe = function (id) {
      var dv = propres && propres[id]; if (!dv) return H0;
      return Hs[dv.nom] || (Hs[dv.nom] = chants(habillage(dv.tex, dv.hex && !dv.tex ? null : texPiece(T, dv.url), dv.hex || dv.repli)));
    };
    var MP = machine(plan);
    MP.forEach(function (p) { group.add(pieceMesh(p, habillageDe(p.id))); });
    /* R39-b (Romain : « modélisé en 3D d'après le DXF, jamais un trou traversant » ; AUDIT Tour 1175) : l'évier monobloc, pièce
       entière entre les 2 bouts de plan, dessus au niveau du plan, corps de 200 mm qui descend dans le meuble ; arêtes en trait
       clair (R39-d) ; une jonction sur le dessus de chaque côté où le plan continue. Sans monobloc : rien de créé */
    (plan.decoupes || []).filter(function (d) { return d.forme === 'monobloc' && d.monobloc && d.monobloc.geometrie; }).forEach(function (d) {
      var gm = d.monobloc.geometrie, x0 = num(d.monobloc.x_mm), pl = MP.filter(function (p) { return p.uvPts; })[0] || MP[0]; if (!pl) return;
      var hb = habille(pl, habillageDe(pl.id)), m = new T.Mesh(monoGeo(T, gm, x0, TH, hb.uvTop, hb.uvSide), hb.mats);
      m.castShadow = true; m.receiveShadow = true; m.userData.piece = 'monobloc';
      m.userData.monobloc = { code: gm.code, x_mm: x0, dessus_y: TH, geo: gm };                    /* lu par CP_3d.info() */
      m.add(new T.LineSegments(new T.EdgesGeometry(m.geometry, 25), new T.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.5 })));
      group.add(m);
      var xs = [], zs = [];
      plan.pieces.forEach(function (p) { p.contour.forEach(function (q) { xs.push(num(q[0])); zs.push(num(q[1])); }); });
      var X0 = Math.min.apply(null, xs), X1 = Math.max.apply(null, xs), Z1 = Math.max.apply(null, zs);
      [x0, x0 + gm.l].forEach(function (xj) {
        if (xj < X0 + 0.5 || xj > X1 - 0.5) return;
        group.add(new T.Line(new T.BufferGeometry().setFromPoints([new T.Vector3(xj, TH + 0.6, 0), new T.Vector3(xj, TH + 0.6, Math.min(Z1, gm.p))]), new T.LineBasicMaterial({ color: SEAM })));
      });
    });
    /* R39-c (Romain : « si c'est un évier HPL ou dans la matière […] bien sûr qu'on doit le modéliser » ; AUDIT Tour 1113) : un évier
       intégré (cuve dans _cfg_model.decoupes) n'est pas un trou traversant. Sous la découpe percée : ses parois au décor, du dessus
       du plan jusqu'à sa profondeur (DXF du fabricant, B et C : 182 mm sous le dessus) ; son fond au décor (évier HPL, 12 mm) ou
       inox (cuve et bac, 13 mm) ; la bonde carrée de l'évier HPL (115 mm, DXF B) ; l'égouttoir à gauche, plateau à 18 mm sous le
       dessus (DXF A), sa face vers la cuve faisant cloison. Matières créées seulement s'il y a une cuve : image d'un plan sans évier
       inchangée. Le trou de robinet reste percé. */
    if (cuves.length) {
      /* R39-d (UX, AUDIT Tour 1122) : arêtes de la cuve en trait clair, lisibles sur un décor sombre (évier tout HPL) */
      var areteCuve = new T.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.5 });
      var inoxMat = null, bondeMat = null, rect4 = function (r) { var x = num(r.x), y = num(r.y), l = num(r.l), pp = num(r.p); return [[x, y], [x + l, y], [x + l, y + pp], [x, y + pp]]; };
      var uvT = function (q) { return [q[0] / H0.tl, q[1] / H0.tht]; }, uvS = function (sAlong, h) { return [sAlong / H0.tl, h / H0.tht]; };
      var bloc = function (pts, y0, y1, ms, trous) { var m = new T.Mesh(slab(T, pts, pts.map(function () { return true; }), y0, y1, uvT, uvS, null, trous || []), ms); m.castShadow = true; m.receiveShadow = true; m.userData.cuve = true; return m; };
      cuves.forEach(function (h) {
        /* évier intégré Dekton (pans_mm : schéma du tarif, « fondo a spicchi ») : fond en 4 pans qui descendent des parois, à la
           profondeur de la cuve, jusqu'à la bonde ronde au centre, pans_mm plus bas ; parois jusqu'au point le plus bas */
        var c = h.cuve, P = num(c.profondeur_mm), pans = num(c.fond_pans_mm), bas = TH - P - pans, ext = h, int0 = c.interieur_plan_mm ? rect4(c.interieur_plan_mm) : null; if (!int0) return;
        var eg = c.egouttoir_plan_mm ? rect4(c.egouttoir_plan_mm) : null, inox = c.fond === 'inox', ep = inox ? 13 : 12;
        var tout = eg ? [[Math.min(eg[0][0], int0[0][0]), Math.min(eg[0][1], int0[0][1])], [Math.max(eg[1][0], int0[1][0]), Math.min(eg[1][1], int0[1][1])], [Math.max(eg[2][0], int0[2][0]), Math.max(eg[2][1], int0[2][1])], [Math.min(eg[3][0], int0[3][0]), Math.max(eg[3][1], int0[3][1])]] : int0;
        var paroi = bloc(ext, bas, TH, mats, [{ pts: tout, g: 1 }]); paroi.add(new T.LineSegments(new T.EdgesGeometry(paroi.geometry, 25), areteCuve)); group.add(paroi);   /* parois, avec le rebord vu du dessus */
        paroi.userData.cuve = { fond: inox ? 'inox' : 'decor', profondeur_mm: P, egouttoir: !!eg, bonde_mm: num(c.bonde_mm) || 0 };   /* lu par CP_3d.info() */
        if (pans > 0) { paroi.userData.cuve.pans_mm = pans; paroi.userData.cuve.bonde = 'ronde'; }
        paroi.userData.cuveInt = int0; paroi.userData.cuveTH = TH;      /* relevé des verticales (CP_3d.info) */
        if (inox && !inoxMat) inoxMat = new T.MeshStandardMaterial({ color: 0xc5cacf, metalness: 0.6, roughness: 0.35 });
        var cx = (int0[0][0] + int0[2][0]) / 2, cz = (int0[0][1] + int0[2][1]) / 2, yb = bas + ep;   /* centre du fond ; dessus de la bonde */
        if (pans > 0) {
          var fp = new T.Mesh(pansGeo(T, int0, [cx, cz], TH - P, bas, uvT), mats); fp.castShadow = true; fp.receiveShadow = true; fp.userData.cuve = true;
          fp.add(new T.LineSegments(new T.EdgesGeometry(fp.geometry, 1), areteCuve)); group.add(fp);   /* les 4 pans, arêtes vers la bonde comme le schéma */
          group.add(bloc(int0, bas - ep, bas, mats));                    /* dessous, fermé */
          yb = bas;
        } else { var fd = bloc(int0, bas, bas + ep, inox ? inoxMat : mats); fd.add(new T.LineSegments(new T.EdgesGeometry(fd.geometry, 25), areteCuve)); group.add(fd); }   /* fond */
        if (eg) { var pe = bloc(eg, bas, TH - 18, mats); pe.add(new T.LineSegments(new T.EdgesGeometry(pe.geometry, 25), areteCuve)); group.add(pe); }   /* égouttoir : plateau à 18 mm sous le dessus */
        var bo = num(c.bonde_mm); if (bo > 0) {
          if (!bondeMat) bondeMat = new T.MeshStandardMaterial({ color: 0x3f4246, metalness: 0.5, roughness: 0.5 });
          var bp = []; if (pans > 0) { for (var k = 0; k < 32; k++) { var a = k / 32 * 2 * Math.PI; bp.push([cx + Math.cos(a) * bo / 2, cz + Math.sin(a) * bo / 2]); } }   /* bonde ronde (Dekton) */
          else bp = [[cx - bo / 2, cz - bo / 2], [cx + bo / 2, cz - bo / 2], [cx + bo / 2, cz + bo / 2], [cx - bo / 2, cz + bo / 2]];   /* bonde carrée (HPL, DXF B) */
          group.add(bloc(bp, yb, yb + (pans > 0 ? 3 : 1.5), bondeMat));   /* sur les pans, un peu plus haute pour rester vue */
        }
      });
    }
    /* jonctions : trait fin sur le dessus, comme sur le dessin */
    (plan.jonctions || []).forEach(function (j) {
      var a = j.segment[0], b = j.segment[1], g = new T.BufferGeometry().setFromPoints([new T.Vector3(num(a[0]), TH + 0.6, num(a[1])), new T.Vector3(num(b[0]), TH + 0.6, num(b[1]))]);
      group.add(new T.Line(g, new T.LineBasicMaterial({ color: SEAM })));
    });
    /* jambages */
    var legH = 0;
    ((plan.jambages && plan.jambages.pieces) || []).forEach(function (leg) {
      var f = legFootprint(plan, leg); if (!f) return;
      legH = Math.max(legH, f.h);
      var r = rotOf(f.fil), c = Math.cos(r), s = Math.sin(r), o = f.pts[0];
      var uvTop = function (q) { var x = q[0] - o[0], y = q[1] - o[1]; return [(x * c + y * s) / tl, (-x * s + y * c) / tht]; };
      var uvSide = function (sAlong, h) { return fil === 'y' ? [sAlong / tl, h / tht] : [h / tl, sAlong / tht]; };   /* fil vertical */
      if (ftex && FIT) {
        /*  la joue aussi d'une seule image, fil vertical : boîte = profondeur × hauteur dans le repère de l'image*/
        var dep = 0; for (var k = 0; k < f.pts.length; k++) { var q2 = f.pts[(k + 1) % f.pts.length]; dep = Math.max(dep, Math.hypot(q2[0] - f.pts[k][0], q2[1] - f.pts[k][1])); }
        var FL = ftex.fil === 'x' ? FIT(ftex, f.h, dep, true) : FIT(ftex, dep, f.h, true);
        uvSide = function (sAlong, h) { return ftex.fil === 'x' ? [Math.abs(h) / FL.w, sAlong / FL.h] : [sAlong / FL.w, Math.abs(h) / FL.h]; };
      }
      /*  la joue est une pièce à
         part. Elle s'arrête 1,5 mm sous le plan (ligne d'ombre lisible ; avant, son dessus et le dessous du plan étaient
         dans le même plan et scintillaient) et elle est rentrée de 1 mm sur ses quatre côtés.*/
      var ctr = [0, 0]; f.pts.forEach(function (q) { ctr[0] += q[0] / f.pts.length; ctr[1] += q[1] / f.pts.length; });
      var ad = [f.pts[1][0] - f.pts[0][0], f.pts[1][1] - f.pts[0][1]], an = [f.pts[3][0] - f.pts[0][0], f.pts[3][1] - f.pts[0][1]];
      var la = Math.hypot(ad[0], ad[1]) || 1, ln = Math.hypot(an[0], an[1]) || 1; ad = [ad[0] / la, ad[1] / la]; an = [an[0] / ln, an[1] / ln];
      var inPts = f.pts.map(function (q) {
        var v = [ctr[0] - q[0], ctr[1] - q[1]], sd = (v[0] * ad[0] + v[1] * ad[1]) > 0 ? 1 : -1, sn = (v[0] * an[0] + v[1] * an[1]) > 0 ? 1 : -1;
        return [q[0] + (ad[0] * sd + an[0] * sn) * LEG_IN, q[1] + (ad[1] * sd + an[1] * sn) * LEG_IN];
      });
      var mesh = new T.Mesh(slab(T, inPts, [1, 1, 1, 1], -f.h, -LEG_GAP, uvTop, uvSide), [decorMat, decorMat, decorMat, decorMat]);
      mesh.castShadow = true; mesh.receiveShadow = true; group.add(mesh);
    });
    /*  pièce debout, face décor vers la vue ; le haut du dessin (y = 0) en haut ; sur le fond
       neutre habituel, sans mur, ombre discrète au sol.
       Rotation de +90° autour de x : (x, y, z) → (x, −z, y), puis relevée de sa hauteur. Vue de face, de trois quarts.*/
    /* Étagère murale : posée contre un mur neutre, sans sol ; vue de face*/
    if (/etagere|boiserie/i.test((plan.produit && plan.produit.handle) || '')) {   /* R133 : la boiserie, même scène que l'étagère */
      /* V2 (cp-etagere) : chaque étagère à sa hauteur (z_dessous_mm), supports d'après les profils du DXF
         (KALABRONE : profil de 9 sommets, plaque au mur 5 mm derrière l'étagère ; équerre du kit en L : bras de 234 mm,
         jambe de 96 mm, jambe de force), extrudés sur leur largeur (20 et 30 mm)*/
      var et = plan.etageres, fxE = plan.fixation || {};
      if (et && et.liste) {
        var byId = {}; et.liste.forEach(function (s) { byId[s.id] = s; });
        group.children.forEach(function (m) {
          var s = m.userData && byId[m.userData.piece]; if (!s) return;
          if (et.mur) {                                             /* compositeur de mur : la pièce à sa place ; un montant debout */
            if (s.orientation === 'verticale') { m.rotation.z = Math.PI / 2; m.position.x = num(s.x_gauche_mm) + TH; }
            else m.position.x = num(s.x_gauche_mm);
          }
          m.position.y = num(s.z_dessous_mm);
          /* une étagère est plaquée sur toutes ses faces (chants tous posés) : dessous au décor, chants au décor */
          if (Array.isArray(m.material) && m.material.length >= 4) m.material = [m.material[0], m.material[1], m.material[1], m.material[0]];
        });
        var supCol = { blanc: 0xf1f0ec, noir: 0x222222, 'satiné': 0xb9bdc1 }[fxE.couleur || 'satiné'] || 0xb9bdc1;
        var supMat = new T.MeshStandardMaterial({ color: supCol, roughness: fxE.couleur === 'satiné' || fxE.type === 'kitL' ? 0.35 : 0.6, metalness: fxE.couleur === 'satiné' || fxE.type === 'kitL' ? 0.55 : 0.05 });
        /* profil (u : depuis l'arrière de l'étagère vers l'avant ; v : hauteur, 0 au dessous de l'étagère) → volume */
        var prof = function (pts, w, x, zb) {
          var sh = new T.Shape(); pts.forEach(function (q, i) { if (i) sh.lineTo(q[0], q[1]); else sh.moveTo(q[0], q[1]); });
          var geo = new T.ExtrudeGeometry(sh, { depth: w, bevelEnabled: false }), ms = new T.Mesh(geo, supMat);
          ms.rotation.y = -Math.PI / 2; ms.position.set(x + w / 2, zb, 0); ms.castShadow = true; ms.receiveShadow = true;   /* extrusion de x + w/2 à x − w/2 */
          return ms;
        };
        var KAL = [[0, 0], [40, 41.5], [110, 41.5], [110, 55], [3, 55], [3, 80], [40, 80], [40, 83], [0, 83]].map(function (q) { return [q[0] - 5, q[1] - 42]; });
        var ARM = [[1, 0], [235.4, 0], [235.4, -7.5], [1, -7.5]], LEG = [[1, 0], [13, 0], [13, -96.3], [1, -96.3]], STRUT = [[13, -55], [22, -55], [214, -9.4], [204, -7.5]];
        et.liste.forEach(function (s) {
          if (s.orientation === 'verticale') return;                 /* montant : fixation invisible */
          var xs = (s.fixation && s.fixation.supports_x_mm) || [], zb = num(s.z_dessous_mm), x0 = num(s.x_gauche_mm);
          xs.forEach(function (sx) {
            if (fxE.type === 'kalabrone') group.add(prof(KAL, 20, x0 + sx, zb));             /* x : axe du support */
            else if (fxE.type === 'kitL') [ARM, LEG, STRUT].forEach(function (pp) { group.add(prof(pp, 30, x0 + sx, zb)); });
          });
        });
      }
      /* R133 (boiserie, plan validé § 3) : les panneaux, pavés de 18 mm contre le mur, derrière les étagères (de −18 à 0 : les
         étagères et les montants sont avancés de 18 mm devant la boiserie) ; leur décor, à l'échelle de son motif, veinage
         horizontal (une image tournée d'un quart de tour si son fil est vertical) ; aplat en attendant l'image (texPiece) ; pas
         d'ombre portée sur eux (R140, comme le mur) */
      var bois = plan.boiserie || (typeof window.CP_boisModel === 'function' ? window.CP_boisModel() : null), bdec = typeof window.CP_boisDecors === 'function' ? window.CP_boisDecors() : null;
      if (bois && bois.panneaux) bois.panneaux.forEach(function (b) {
        var w = num(b.largeur_mm), h = num(b.hauteur_mm), dv = bdec && bdec[b.id], tx = dv && !(dv.hex && !dv.tex) ? texPiece(T, dv.url) : null, mat;
        if (tx) {
          var t2 = tx.clone(), tl = dv.tex ? num(dv.tex.l) : w, tt = dv.tex ? num(dv.tex.h) : h, vy = dv.tex && dv.tex.fil === 'y';
          t2.wrapS = t2.wrapT = T.MirroredRepeatWrapping; t2.center.set(0.5, 0.5);
          if (vy) { t2.rotation = Math.PI / 2; t2.repeat.set(h / tl, w / tt); } else t2.repeat.set(w / tl, h / tt);
          t2.needsUpdate = true;
          mat = new T.MeshStandardMaterial({ map: t2, roughness: 1, metalness: 0 });
        } else mat = new T.MeshStandardMaterial({ color: lin((dv && (dv.hex || dv.repli)) || '#c8b89a'), roughness: 1, metalness: 0 });
        var pb = new T.Mesh(new T.BoxGeometry(w, h, 18), mat);
        pb.position.set(num(b.x_gauche_mm) + w / 2, num(b.bas_mm) + h / 2, -9); pb.castShadow = false; pb.receiveShadow = false; pb.userData.piece = b.id;
        pb.add(new T.LineSegments(new T.EdgesGeometry(pb.geometry), areteMat));
        group.add(pb);
      });
      var bbE = new T.Box3().setFromObject(group), wx = bbE.max.x - bbE.min.x, hy = bbE.max.y - bbE.min.y, murE = et && et.mur, wall;
      if (murE && num(murE.largeur_mm) > 0 && num(murE.hauteur_mm) > 0) {   /* le mur saisi par le client, à sa taille */
        wall = new T.Mesh(new T.PlaneGeometry(num(murE.largeur_mm), num(murE.hauteur_mm)), new T.MeshStandardMaterial({ color: 0xece7df, roughness: 1 }));
        wall.position.set(num(murE.largeur_mm) / 2, num(murE.hauteur_mm) / 2, Math.min(bbE.min.z, -5) - 0.5);
      } else {
        wall = new T.Mesh(new T.PlaneGeometry(wx + 1200, hy + 1200), new T.MeshStandardMaterial({ color: 0xece7df, roughness: 1 }));
        wall.position.set((bbE.min.x + bbE.max.x) / 2, (bbE.min.y + bbE.max.y) / 2, Math.min(bbE.min.z, -5) - 0.5);
      }
      /* R140 (Romain, 27/10 ; AUDIT Tour 869) : ni le mur ni le sol ne reçoivent l'ombre portée des pièces (vue, vignette, image
         du panier : même scène) ; le relief des pièces reste */
      wall.receiveShadow = false;
      /* A-351 : le sol au pied du mur (cote 0 des étagères ; sans mur saisi, au bas du mur neutre), uni et clair (#d9cbb3),
         1 500 mm devant lui ; hors du cadrage, qui reste celui des étagères et du mur. R128 (Romain, 27/10) : toujours
         affiché (plus de bouton « Sol »), de la largeur exacte du mur (il le dépassait de 300 mm de chaque côté) ; dans
         l'image téléchargée aussi (vue courante). L'image jointe au panier reste sans sol (A-351) */
      var cadreE = new T.Group(); cadreE.add(group); cadreE.add(wall);
      var wp = wall.geometry.parameters, sol = new T.Mesh(new T.PlaneGeometry(wp.width, 1500), new T.MeshStandardMaterial({ color: 0xd9cbb3, roughness: 0.9 }));
      sol.rotation.x = -Math.PI / 2; sol.position.set(wall.position.x, wall.position.y - wp.height / 2, wall.position.z + 750); sol.receiveShadow = false;
      var outerE = new T.Group(); outerE.add(cadreE); outerE.add(sol);
      /* V2 : vue de face, un peu par-dessous, pour voir les supports ; la caméra peut descendre sous l'horizontale (phiMax)*/
      /* É-1 : vue presque de face (7° au lieu de 18°) : le mur se lit comme un seul plan vertical. Hauteur de vue ≈ 15° :
         le dessus et le décor se lisent sur toutes les étagères*/
      /* A-351 : la vue tourne et zoome comme celle des plans, sans passer derrière le mur (±70° autour de sa face), ni sous le
         sol quand il est affiché (φ 1,5 au plus, 1,8 sans lui) */
      return { group: outerE, fit: murE ? cadreE : group, thickness: TH, legH: 0, wall: true, view: { theta: -0.12, phi: 1.3 }, phiMax: 1.8,
        sol: sol, thetaMax: 1.22, phiMin: 0.35, phiMaxSol: 1.5 };
    }
    if (/credence/i.test((plan.produit && plan.produit.handle) || '')) {
      var bb = new T.Box3().setFromObject(group), Hc = bb.max.z;
      var outer = new T.Group();
      group.rotation.x = Math.PI / 2; group.position.y = Hc; outer.add(group);
      return { group: outer, fit: group, thickness: TH, legH: 0, vertical: true, view: { theta: -0.35, phi: 1.32 } };
    }
    return { group: group, thickness: TH, legH: legH };
  }

  /* ── 4. La 3D DANS le cadre du dessin ──
     Interrupteur « 2D | 3D » (#cp-3d-btn, aria-pressed) à la place de l'ancienne fenêtre « Vue 3D / Retour au plan ».
     La toile se pose sur le dessin (#svg) dans #stage : le plein écran (#stage) est gardé pendant la bascule.
     Transition de 450 ms : fondu croisé et caméra qui descend de la vue de dessus (alignée sur le dessin) à la vue de
       trois quarts ; retour inverse. prefers-reduced-motion : bascule immédiate.
     Échap ramène en 2D ; le focus reste sur l'interrupteur. Les cotes se saisissent en 2D.
     Une seule toile WebGL : la fenêtre des jambages ramène le temps de son aperçu en 2D, puis la 3D revient.
     La 3D suit chaque rendu du moteur (nouveau plan 120 ms après le dernier rendu).*/
  var ui = null, on = false, back3d = false, DUR = 450, anim = null;
  function reduced() { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); }
  function injectCss() {
    if (document.getElementById('cp-3d-css')) return;
    var st = document.createElement('style'); st.id = 'cp-3d-css';
    st.textContent =
      /* au-dessus des cotes du moteur (#dims, z-index 10), sous ses menus et fenêtres (9400 et plus) */
      '#cp-3d{position:absolute;z-index:11;background:#f1ede5;color:#0d3527;opacity:0;transition:opacity .45s ease}' +
      /* dessin peu haut (téléphone) : pas de ligne d'aide, boutons plus petits */
      '#cp-3d.is-small .cp3-help{display:none}#cp-3d.is-small .cp3-tools{right:6px;bottom:6px;gap:4px}' +
      '#cp-3d.is-small button.cp3-b{min-height:34px;min-width:34px;padding:0 8px;font-size:13px}' +
      '#cp-3d.is-on{opacity:1}#cp-3d[hidden]{display:none}' +
      /* en 3D, les cotes du moteur (#dims, dont celles posées hors du dessin) s'effacent avec le même fondu */
      '#stage #dims{transition:opacity .45s ease}#stage.cp3d-on #dims{opacity:0;visibility:hidden;transition:opacity .45s ease,visibility 0s linear .45s}' +
      '@media (prefers-reduced-motion:reduce){#stage #dims,#stage.cp3d-on #dims{transition:none}}' +
      '@media (prefers-reduced-motion:reduce){#cp-3d{transition:none}}' +
      '#cp-3d button.cp3-b{white-space:nowrap;flex:0 0 auto;min-height:40px;min-width:40px;padding:0 12px;border:1px solid #0d3527;border-radius:4px;background:#fff;color:#0d3527;font:inherit;font-size:14px;font-weight:600;cursor:pointer}' +
      /*  bouton « Image » (icône seule quand le cadre est étroit)*/
      '#cp-3d button.cp3-dl{display:inline-flex;align-items:center;gap:6px}#cp-3d.is-small .cp3-dl-t{display:none}' +
      /* R218 : l'icône à 18 px par le CSS ; la règle « svg{width:100%;height:100%} » du moteur (configurator-custom.css) passe
         avant ses attributs, et Safari sur iPhone l'étirait, la barre avec elle */
      '#cp-3d button.cp3-dl svg{flex:0 0 18px;width:18px;height:18px}' +
      '@media (max-width:480px){#cp-3d .cp3-dl-t{display:none}#cp-3d button.cp3-dl{padding:0 10px}}' +
      '#cp-3d button.cp3-b:hover{background:#0d3527;color:#fff}#cp-3d button:focus-visible{outline:2px solid #80602f;outline-offset:2px}' +
      /* hors plein écran, glisser verticalement fait défiler la page ; en plein écran, contrôle complet */
      '#cp-3d canvas{display:block;width:100%;height:100%;touch-action:pan-y;cursor:grab;outline:none}' +
      '#stage.cfgfs-on #cp-3d canvas,#stage.is-fullscreen-mobile #cp-3d canvas{touch-action:none}' +
      '#cp-3d .cp3-h-fs,#stage.cfgfs-on #cp-3d .cp3-h-page,#stage.is-fullscreen-mobile #cp-3d .cp3-h-page{display:none}' +
      '#stage.cfgfs-on #cp-3d .cp3-h-fs,#stage.is-fullscreen-mobile #cp-3d .cp3-h-fs{display:inline}' +
      '#cp-3d canvas:active{cursor:grabbing}#cp-3d canvas:focus-visible{box-shadow:inset 0 0 0 3px #80602f}' +
      '#cp-3d .cp3-tools{position:absolute;right:10px;bottom:10px;display:flex;gap:6px}' +
      '#cp-3d .cp3-help{position:absolute;left:10px;bottom:10px;max-width:min(58%,420px);margin:0;padding:6px 10px;border-radius:4px;background:rgba(255,255,255,.9);font-size:13px;line-height:1.35;color:#374151}' +
      '#cp-3d .cp3-msg{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;padding:18px;text-align:center;font-size:15px;line-height:1.45}' +
      '#cp-3d .cp3-msg[hidden],#cp-3d .cp3-tools[hidden],#cp-3d .cp3-help[hidden]{display:none}' +
      '@media (max-width:600px){#cp-3d .cp3-help{max-width:none;right:10px;bottom:56px}}' +
      /* interrupteur : le mode courant en plein */
      '#cp-3d-btn .cp3-seg{display:inline-block;min-width:26px;padding:1px 5px;border-radius:3px;text-align:center}' +
      '#cp-3d-btn[aria-pressed="false"] .cp3-seg[data-m="2d"],#cp-3d-btn[aria-pressed="true"] .cp3-seg[data-m="3d"]{background:#0d3527;color:#fff}' +
      '#cp-3d-btn[aria-disabled="true"]{opacity:.55;cursor:not-allowed}' +
      /* vue d'ensemble fixe (étagère) : ni zoom ni recentrage ; la toile se clique */
      '#cp-3d.is-fixed .cp3-b[data-a="in"],#cp-3d.is-fixed .cp3-b[data-a="out"],#cp-3d.is-fixed .cp3-b[data-a="reset"]{display:none}' +
      '#cp-3d.is-fixed canvas{cursor:pointer}#cp-3d.is-fixed canvas:active{cursor:pointer}';
    document.head.appendChild(st);
  }
  var TOGGLE = '<span class="cp3-seg" data-m="2d">2D</span><span class="cp3-seg" data-m="3d">3D</span>';
  function setBtn() {
    var b = document.getElementById('cp-3d-btn'); if (!b) return;
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
  }
  function ensureButton() {
    var bar = document.getElementById('stage-toolbar'); if (!bar) return false;
    injectCss();
    var b = document.getElementById('cp-3d-btn');
    if (!b) {
      /*  classe de « Plein écran », donc son style à chaque largeur ; d'ordinaire déjà posé
         par cp-decor-data (avant le moteur), avec le même contenu*/
      b = document.createElement('button'); b.type = 'button'; b.id = 'cp-3d-btn'; b.className = 'btn-fullscreen';
      var fs = document.getElementById('btn-fullscreen'); bar.insertBefore(b, fs || null);
    }
    if (b.__cp3d) return true;
    b.__cp3d = true;
    b.removeAttribute('aria-haspopup');
    if (b.innerHTML.indexOf('cp3-seg') < 0) b.innerHTML = TOGGLE;
    b.setAttribute('aria-label', 'Vue 3D'); setBtn();
    b.addEventListener('click', function () { if (on) exit(); else enter(); });
    b.addEventListener('keydown', function (e) { if (e.key === 'Escape' && on) { e.preventDefault(); exit(); } });
    return true;
  }

  function buildUi() {
    var stage = document.getElementById('stage'); if (!stage) return null;
    var d = document.createElement('div');
    d.id = 'cp-3d'; d.hidden = true;
    d.setAttribute('role', 'region'); d.setAttribute('aria-label', 'Plan en 3D');
    d.innerHTML = '<canvas tabindex="0"></canvas>' +
      '<p class="cp3-help"></p>' +
      '<div class="cp3-tools"><button type="button" class="cp3-b" data-a="out" aria-label="Zoom arrière">−</button><button type="button" class="cp3-b" data-a="in" aria-label="Zoom avant">+</button><button type="button" class="cp3-b" data-a="reset">Recentrer</button>' +
      '<button type="button" class="cp3-b cp3-dl" data-a="image" aria-label="Télécharger l\'image de la vue 3D" title="Télécharger l\'image"><svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 4v11"/><path d="m7 10 5 5 5-5"/><path d="M5 20h14"/></svg><span class="cp3-dl-t">Image</span></button></div>' +
      '<div class="cp3-msg" role="status" aria-live="polite"></div>';
    var svg = document.getElementById('svg');
    stage.insertBefore(d, svg ? svg.nextSibling : stage.firstChild);
    ui = { root: d, canvas: d.querySelector('canvas'), msg: d.querySelector('.cp3-msg'), help: d.querySelector('.cp3-help'), tools: d.querySelector('.cp3-tools') };
    d.addEventListener('click', function (e) {
      var a = e.target.closest('[data-a]'); if (!a) return;
      var k = a.getAttribute('data-a');
      if (k === 'close') exit();
      else if (k === 'in') zoom(0.8); else if (k === 'out') zoom(1.25); else if (k === 'reset') resetView();
      else if (k === 'image') downloadImage();
    });
    d.addEventListener('keydown', function (e) {
      var of = ovr(); if (of && of.fixed) return;                          /* vue d'ensemble fixe : ni Échap vers la 2D, ni flèches */
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); exit(); var b = document.getElementById('cp-3d-btn'); if (b) b.focus(); return; }
      if (!view || e.target !== ui.canvas) return;
      var st = 0.12;
      if (e.key === 'ArrowLeft') { view.theta -= st; } else if (e.key === 'ArrowRight') { view.theta += st; }
      else if (e.key === 'ArrowUp') { view.phi = Math.max(0.12, view.phi - st); } else if (e.key === 'ArrowDown') { view.phi = Math.min((view.model && view.model.phiMax) || 1.45, view.phi + st); }
      else if (e.key === '+' || e.key === '=') { zoom(0.85); return; } else if (e.key === '-') { zoom(1.18); return; }
      else if (e.key === 'Home' || e.key === '0') { resetView(); return; } else return;
      e.preventDefault(); moved(); bornesVue(); place();
    }, true);
    /* la toile suit le cadre du dessin (largeur, plein écran, fitStage) */
    if (window.ResizeObserver) { var ro = new ResizeObserver(function () { if (on) { place3d(); resize(); } }); ro.observe(stage); if (svg) ro.observe(svg); }
    window.addEventListener('resize', function () { if (on) setTimeout(function () { place3d(); resize(); }, 60); });
    return ui;
  }
  /* la toile couvre exactement le dessin (#svg), dans #stage */
  function place3d() {
    if (!ui) return;
    var stage = document.getElementById('stage'), svg = document.getElementById('svg'); if (!stage || !svg) return;
    var s = stage.getBoundingClientRect(), r = svg.getBoundingClientRect(), st = ui.root.style;
    st.left = Math.round(r.left - s.left - stage.clientLeft + stage.scrollLeft) + 'px';
    st.top = Math.round(r.top - s.top - stage.clientTop + stage.scrollTop) + 'px';
    st.width = Math.round(r.width) + 'px'; st.height = Math.round(r.height) + 'px';
    ui.root.classList.toggle('is-small', r.height < 320);
  }

  var view = null;
  /* A-351 : bornes de la vue d'un modèle qui en donne (étagère : θ ±thetaMax, φ de phiMin à phiMax, phiMaxSol avec le sol) ;
     sans elles, celles des plans (φ de 0,12 à phiMax ou 1,45, θ libre) */
  function bornesVue() {
    var m = view.model || {}, tm = m.thetaMax, pmax = m.sol && m.sol.visible ? m.phiMaxSol : (m.phiMax || 1.45);
    if (tm) view.theta = Math.max(-tm, Math.min(tm, view.theta));
    view.phi = Math.max(m.phiMin || 0.12, Math.min(pmax, view.phi));
  }
  /*  sans terme technique*/
  var NO_GL = '<p><strong>La vue 3D n\'est pas disponible sur ce navigateur.</strong></p><p>Votre plan reste visible et modifiable en 2D.</p><button type="button" class="cp3-b" data-a="close">Revenir au plan 2D</button>';
  function msg(html, busy) {
    ui.msg.innerHTML = html; ui.msg.hidden = !html;
    ui.canvas.style.visibility = html && !busy ? 'hidden' : '';
    ui.tools.hidden = !!html; ui.help.hidden = !!html;
  }

  function enter() {
    var A = getApp(); if (on || !A || typeof window.CP_plan !== 'function') return;
    if (!ui && !buildUi()) return;
    on = true; setBtn();
    /* aide selon l'appareil : au doigt, ni molette ni Échap*/
    var touch = !!(window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches);
    /*  au doigt, hors plein écran, un glissé vertical fait défiler la page (touch-action
       pan-y, voulu) : l'aide le dit ; en plein écran, tout tourne*/
    if (touch) ui.help.innerHTML = '<span class="cp3-h-page">Glissez à l\'horizontale pour tourner, pincez pour zoomer ; plein écran pour incliner.</span>' +
      '<span class="cp3-h-fs">Glissez pour tourner et incliner, pincez pour zoomer.</span> Les cotes se saisissent en 2D.';
    else ui.help.textContent = 'Faites glisser pour tourner, molette pour zoomer ; Échap pour revenir en 2D. Les cotes se saisissent en 2D.';
    ui.canvas.setAttribute('aria-label', touch ? 'Plan en 3D : glissez pour tourner, pincez pour zoomer' : 'Plan en 3D : faites glisser pour tourner, molette ou pincement pour zoomer ; au clavier, flèches et + / −');
    var of = ovr();                                                          /* vue d'ensemble fixe (étagère) : son aide */
    ui.root.classList.toggle('is-fixed', !!(of && of.fixed));
    if (of && (of.fixed || of.help)) { ui.help.textContent = of.help || ''; ui.canvas.setAttribute('aria-label', of.label || 'Vue d\'ensemble'); }   /* A-351 : l'étagère, mobile, garde son aide */
    ui.root.hidden = false; place3d();
    /*  à 1 440 et 1 920 px, le bas de la vue et sa ligne d'aide tombaient sous l'écran (+197 px) :
       on fait défiler juste ce qu'il faut pour voir toute la vue (hors plein écran)*/
    if (!document.fullscreenElement) try { ui.root.scrollIntoView({ block: 'nearest', behavior: reduced() ? 'auto' : 'smooth' }); } catch (e) {}
    var stg = document.getElementById('stage'); if (stg) stg.classList.add('cp3d-on');
    requestAnimationFrame(function () { if (on) ui.root.classList.add('is-on'); });   /* fondu (CSS, 450 ms) */
    if (!hasWebGL()) { msg(NO_GL); return; }
    var cached = !!(window.THREE && window.THREE.WebGLRenderer);
    msg(cached ? '<p>Préparation de la vue 3D…</p>' : '<p>Chargement de la vue 3D…</p>', true);
    loadThree().then(function (T) { if (on) start(T); }).catch(function () {
      if (on) msg('<p><strong>La vue 3D n\'a pas pu se charger.</strong> Vérifiez votre connexion puis réessayez.</p><p>Votre plan reste visible et modifiable en 2D.</p><button type="button" class="cp3-b" data-a="close">Revenir au plan 2D</button>');
    });
  }
  function exit(instant) {
    if (!on || !ui) return;
    on = false; setBtn();
    var done = function () { if (on) return; ui.root.hidden = true; stop(); };
    ui.root.classList.remove('is-on');
    var stg = document.getElementById('stage'); if (stg) stg.classList.remove('cp3d-on');
    if (instant || reduced() || !view) { done(); return; }
    animateTo(fromView(), DUR, null);                                       /* la caméra remonte pendant le fondu */
    setTimeout(done, DUR + 20);
  }
  /* vue de départ : de dessus, orientée comme le dessin 2D (x à droite, profondeur vers le bas) ; crédence : de face */
  function fromView() { return view && view.model && view.model.vertical ? { theta: 0, phi: 1.45 } : { theta: 0, phi: 0.06 }; }
  /*
     presque de face, la longueur à l'horizontale ; le chant avant descend doucement de gauche à droite (caméra un peu
     à droite de l'axe, θ = +0,21 : environ 6° à l'écran, mesuré par CP_3d.info.cadre) ; élévation d'environ 27°
     (φ = 1,10 depuis la verticale), dessus et chant avant lisibles ; L, U et G vus de face, le fond en haut.
     Crédence : sa vue de face.*/
  var VIEW = { theta: 0.21, phi: 1.18 };                                   /* un peu plus bas : le chant avant se voit mieux */
  function toView() { var pv = view && view.model && view.model.view; return pv ? { theta: pv.theta, phi: pv.phi } : { theta: VIEW.theta, phi: VIEW.phi }; }
  function animateTo(to, dur, cb) {
    if (!view) return;
    if (anim) cancelAnimationFrame(anim.raf);
    var th0 = view.theta, ph0 = view.phi, r0 = view.radius;
    view.theta = to.theta; view.phi = to.phi; var r1 = fitRadius(); view.theta = th0; view.phi = ph0;
    var t0 = performance.now(), ease = function (x) { return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2; };
    var step = function (now) {
      if (!view) return;
      var k = Math.min(1, (now - t0) / dur), e = ease(k);
      view.theta = th0 + (to.theta - th0) * e; view.phi = ph0 + (to.phi - ph0) * e; view.radius = r0 + (r1 - r0) * e;
      place();
      if (k < 1) anim = { raf: requestAnimationFrame(step) }; else { anim = null; view.fitR = r1; if (cb) cb(); }
    };
    anim = { raf: requestAnimationFrame(step) };
  }
  /* un rendu du moteur pendant la 3D : nouveau plan, caméra gardée */
  var refreshT = 0;
  function refreshSoon() {
    if (!on || !view) return;
    clearTimeout(refreshT);
    refreshT = setTimeout(function () { if (on && view && view.rebuild) view.rebuild(window.CP_plan()); }, 120);
  }
  function open() { enter(); }
  function close() { exit(); }
  function stop() {
    if (!view) return;
    window.removeEventListener('resize', view.onResize);
    try { view.renderer.dispose(); view.renderer.forceContextLoss(); } catch (e) {}
    view.dispose.forEach(function (x) { try { x.dispose(); } catch (e) {} });
    view = null;
    /* toile neuve : un contexte WebGL perdu ne se recrée pas sur la même toile */
    var old = ui.canvas, c = document.createElement('canvas');
    c.tabIndex = 0; c.setAttribute('aria-label', old.getAttribute('aria-label'));
    old.parentNode.replaceChild(c, old); ui.canvas = c;
  }

  function start(T) {
    if (!on) return;
    stop();
    var plan = window.CP_plan();
    var canvas = ui.canvas, renderer;
    try { renderer = new T.WebGLRenderer({ canvas: canvas, antialias: true, alpha: false }); }
    catch (e) { msg(NO_GL); return; }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputEncoding = T.sRGBEncoding;
    renderer.shadowMap.enabled = true; renderer.shadowMap.type = T.PCFSoftShadowMap;
    renderer.setClearColor(0xf1ede5, 1);
    var scene = new T.Scene(), camera = new T.PerspectiveCamera(35, 1, 10, 100000), dispose = [renderer];
    var L3 = lumieres(T, scene), sun = L3.sun, fill = L3.fill;
    view = { renderer: renderer, scene: scene, camera: camera, theta: VIEW.theta, phi: VIEW.phi, radius: 5000, target: new T.Vector3(), R: 2000, dispose: dispose, queued: false };
    var A = getApp(), url = A && A.getWorkImageUrl ? A.getWorkImageUrl() : null, texture = null;
    var build = function () {
      if (view.model) { lueur(view.model.group, true); scene.remove(view.model.group); disposeGroup(view.model.group); }
      var m = buildScene(T, view.plan || plan, texture); view.model = m; scene.add(m.group);
      var box = new T.Box3().setFromObject(m.fit || m.group), size = box.getSize(new T.Vector3()), c = box.getCenter(new T.Vector3());   /*  cadrage sans le mur*/
      view.target.copy(c); view.center = c.clone(); view.R = Math.max(size.length() / 2, 300); view.box = box;
      view.fitPts = modelPoints(T, m.fit || m.group);                        /*  cadrage sur les vrais sommets*/
      lueur(m.group);                                                         /* après le cadrage : le contour ne le change pas */
      var floorY = m.vertical ? -1 : m.legH ? -m.legH - 1 : -60;            /* sans jambages : posé un peu au-dessus du sol, ombre visible ; crédence : posée au sol*/
      if (!view.floor) {
        var fg = new T.PlaneGeometry(1, 1); dispose.push(fg);
        view.floor = new T.Mesh(fg, new T.ShadowMaterial({ opacity: 0.22 })); view.floor.rotation.x = -Math.PI / 2; view.floor.receiveShadow = true; scene.add(view.floor);
      }
      view.floor.material.opacity = m.wall ? 0 : m.vertical ? 0.1 : 0.22;   /*  ombre discrète sous la crédence ; étagère : pas de sol*/
      view.floor.scale.set(size.x * 3 + 4000, size.z * 3 + 4000, 1); view.floor.position.set(c.x, floorY, c.z);
      sun.position.set(c.x - view.R * 1.1, c.y + view.R * 1.6, c.z + view.R * 1.3); sun.target.position.copy(c);
      /* É-1 : sur le mur, l'ombre de
         chaque étagère faisait un grand parallélogramme gris, lu comme un pan de mur de biais. Lumière presque de face,
         un peu d'en haut : ombre de contact courte (≈ 0,25 × profondeur sous l'étagère). Étagère seulement (mur).*/
      if (m.wall) sun.position.set(c.x - view.R * 0.25, c.y + view.R * 0.6, c.z + view.R * 2.4);
      fill.position.set(c.x + view.R * 1.5, c.y + view.R * 0.8, c.z - view.R * 1.2); fill.target.position.copy(c);
      placeFenetre(L3.win, c, view.R, m.wall);
      var sc = sun.shadow.camera, e = view.R * 1.6; sc.left = -e; sc.right = e; sc.top = e; sc.bottom = -e; sc.near = 10; sc.far = view.R * 8; sc.updateProjectionMatrix();
      if (!view.fitted) {                                                     /* 1re image : de dessus, puis la caméra descend*/
        view.fitted = true;
        if (reduced()) resetView();
        else { var f = fromView(); view.theta = f.theta; view.phi = f.phi; view.radius = fitRadius(); place(); animateTo(toView(), DUR, null); }
      } else {
        /*  le plan a changé (longueur, forme) et le client n'a pas bougé la vue : on recadre, même angle.
           Avant : distance gardée, un plan passé de 2 400 à 4 180 mm sortait du cadre.*/
        if (!view.userMoved && !anim) { view.target.copy(c); view.radius = fitRadius(); view.fitR = view.radius; }
        place();
      }
    };
    var texUrl = null;
    var loadTex = function (u) {
      texUrl = u; if (!u) { texture = null; return; }
      var loader = new T.TextureLoader(); loader.setCrossOrigin('anonymous');
      loader.load(u, function (tx) {
        if (!view || view.renderer !== renderer || texUrl !== u) { tx.dispose(); return; }
        tx.wrapS = tx.wrapT = T.MirroredRepeatWrapping; tx.flipY = false; tx.encoding = T.sRGBEncoding;
        tx.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy ? renderer.capabilities.getMaxAnisotropy() : 1);
        texture = tx; dispose.push(tx); build(); requestRender();
      }, undefined, function () { if (view && view.renderer === renderer) { build(); requestRender(); } });
    };
    loadTex(url);
    /*  chaque rendu du moteur pendant la 3D (refreshSoon) : nouveau plan, décor rechargé s'il a changé*/
    view.rebuilds = 0;
    view.rebuild = function (p) {
      view.plan = p; view.rebuilds++;
      var B = getApp(), u = B && B.getWorkImageUrl ? B.getWorkImageUrl() : null;
      if (u !== texUrl) loadTex(u);
      build(); requestRender();
    };
    msg('');
    resize();                                                                 /* format de l'écran connu avant le cadrage */
    build();
    view.onResize = function () { resize(); if (!view.userMoved) resetView(); };
    window.addEventListener('resize', view.onResize);
    bindPointer(canvas);                                                      /* le focus reste sur l'interrupteur*/
  }

  function moved() { if (view) view.userMoved = true; }
  function resize() {
    if (!view) return;
    var r = ui.canvas.parentNode.getBoundingClientRect(), w = Math.max(1, Math.round(r.width)), h = Math.max(1, Math.round(r.height));
    view.renderer.setSize(w, h, false); view.camera.aspect = w / h; view.camera.updateProjectionMatrix(); place();
  }
  function place() {
    if (!view) return;
    var t = view.target, r = view.radius, sp = Math.sin(view.phi);
    view.camera.position.set(t.x + r * sp * Math.sin(view.theta), t.y + r * Math.cos(view.phi), t.z + r * sp * Math.cos(view.theta));
    view.camera.lookAt(t);
    applyOffset();
    requestRender();
  }
  /*  décalage de l'image (en fraction de l'écran, converti à la taille courante) qui centre le modèle dans la
     zone libre, au-dessus des boutons*/
  function applyOffset() {
    var cam = view.camera, o = view.off, s = view.renderer.getSize(new window.THREE.Vector2());
    if (!o || !s.x || !s.y) { if (cam.view && cam.view.enabled) cam.clearViewOffset(); return; }
    cam.setViewOffset(s.x, s.y, o.nx * s.x / 2, o.ny * s.y / 2, s.x, s.y);
  }
  /* Vue d'ensemble FIXE (étagère, lot 1 : cp-etagere fournit window.CP_3dOverview) : { fixed, help, onPick(idPièce),
     onFrame(project) }. Caméra figée (ni glisser, ni molette), clic ou appui sur une pièce → onPick ; après chaque image,
     onFrame reçoit project(x, y, z) → { x, y } en px dans la vue (pastilles HTML posées par cp-etagere). */
  function ovr() { try { return typeof window.CP_3dOverview === 'function' ? window.CP_3dOverview() : null; } catch (e) { return null; } }
  /* Surbrillance d'une pièce (la pièce choisie dans le panneau de cp-etagere) : CP_3d.highlight(idPièce | null). Un contour
     vert foncé autour de la pièce (coque un peu plus grande, faces arrière seules : seul son bord dépasse), sans toucher à
     ses matériaux (une teinte changeait la couleur du décor) ; retiré et libéré au changement ; réappliqué à chaque
     reconstruction ; jamais dans l'image téléchargée. Sans appel, rien ne change. */
  var hlId = null, LUEUR = 0x0d3527, LUEUR_E = 12;                  /* couleur de la marque ; bord de 12 mm au moins */
  /* largeur du bord en mm pour environ 3 px à l'écran, au centre c de la pièce (au téléphone, 12 mm faisaient 1 px) */
  function bordMm(T, c) {
    try {
      var cam = view && view.camera, cv = view && view.renderer && view.renderer.domElement; if (!cam || !cv) return LUEUR_E;
      cam.updateMatrixWorld();
      var a = c.clone().project(cam), b = c.clone().add(new T.Vector3(100, 0, 0)).project(cam), pp = Math.abs(b.x - a.x) / 2 * (cv.clientWidth || cv.width);
      return pp > 0 ? Math.min(60, Math.max(LUEUR_E, 300 / pp)) : LUEUR_E;
    } catch (e) { return LUEUR_E; }
  }
  function lueur(g, off) {
    if (!g || !window.THREE) return;
    var T = window.THREE, pieces = [];
    g.traverse(function (m) { if (m.userData && m.userData.piece && m.geometry) pieces.push(m); });   /* les pièces peuvent être dans un sous-groupe (mur de l'étagère) */
    pieces.forEach(function (m) {
      var u = m.userData, want = !off && hlId != null && String(u.piece) === hlId;
      if (!want && u.lueur) { var h = u.lueur; if (h.parent) h.parent.remove(h); h.geometry.dispose(); h.material.dispose(); u.lueur = null; }
      else if (want && !u.lueur && m.parent) {
        /* dans le repère du groupe des pièces : 12 mm de plus en largeur, en hauteur et vers l'avant, mais pas vers l'arrière
           (le mur est juste derrière : la coque y disparaissait) ; sa face arrière fait un cadre sur le mur */
        m.parent.updateMatrixWorld(true);
        var bb = new T.Box3().setFromObject(m), e = bordMm(T, bb.getCenter(new T.Vector3())), inv = new T.Matrix4().copy(m.parent.matrixWorld).invert();
        bb.applyMatrix4(inv);
        var lo = bb.min.clone().sub(new T.Vector3(e, e, 0)), hi = bb.max.clone().add(new T.Vector3(e, e, e)), sz = hi.clone().sub(lo);
        var col = new T.Color(LUEUR); if (col.convertSRGBToLinear) col.convertSRGBToLinear();   /* sortie en sRGB : le vert de la marque à l'écran */
        var h2 = new T.Mesh(new T.BoxGeometry(sz.x, sz.y, sz.z), new T.MeshBasicMaterial({ color: col, side: T.BackSide }));
        h2.position.copy(lo.clone().add(hi).multiplyScalar(0.5)); h2.userData.lueurDe = u.piece; m.parent.add(h2); u.lueur = h2;
      }
    });
  }
  function highlight(id) {
    var n = id == null || id === '' ? null : String(id); if (n === hlId) return;
    hlId = n; if (view && view.model) { lueur(view.model.group); requestRender(); }
  }
  function project(x, y, z) {
    var T = window.THREE, R = ui.canvas.getBoundingClientRect(), v = new T.Vector3(x, y, z).project(view.camera);
    return { x: (v.x + 1) / 2 * R.width, y: (1 - v.y) / 2 * R.height, w: R.width, h: R.height, front: v.z < 1 };
  }
  function pickAt(x, y) {
    if (!view || !view.model || !window.THREE) return null;
    var T = window.THREE, r = ui.canvas.getBoundingClientRect(), rc = new T.Raycaster();
    rc.setFromCamera(new T.Vector2((x - r.left) / r.width * 2 - 1, -(y - r.top) / r.height * 2 + 1), view.camera);
    var hits = rc.intersectObject(view.model.group, true);
    for (var i = 0; i < hits.length; i++) { var o = hits[i].object; while (o && !(o.userData && o.userData.piece)) o = o.parent; if (o) return o.userData.piece; }
    return null;
  }
  function requestRender() {
    if (!view || view.queued) return; view.queued = true;
    requestAnimationFrame(function () {
      if (!view) return; view.queued = false; view.renderer.render(view.scene, view.camera);
      var o = ovr(); if (o && o.onFrame) try { o.onFrame(project); } catch (e) { console.warn('[cp-3d] vue d\'ensemble', e); }
    });
  }
  /*  cadrage sur la
     boîte du modèle projetée, 6 % de marge sur les côtés et en haut ; en bas, au-dessus des boutons (−, +, Recentrer)
     et de la bulle d'aide, dont la hauteur est mesurée. La contrainte qui bloque en premier (largeur ou hauteur)
     décide ; le modèle est ensuite centré dans cette zone libre par un décalage de l'image (setViewOffset).
      80 % de la largeur avant ; la vue d'ouverture (angle) ne change pas.*/
  var MARGIN = 0.06;
  /* sommets du modèle (repère du monde), au plus ~1 500 : un L ou un U a un coin VIDE dans sa boîte englobante ; en
     perspective, ce coin tirait le cadrage (L à 0,72 de largeur, décalé à droite) */
  function modelPoints(T, obj) {
    var out = [], v = new T.Vector3();
    obj.updateMatrixWorld(true);
    obj.traverse(function (o) {
      if (!o.isMesh || !o.geometry || !o.geometry.attributes.position) return;
      var a = o.geometry.attributes.position, step = Math.max(1, Math.floor(a.count / 400));
      for (var i = 0; i < a.count; i += step) { v.fromBufferAttribute(a, i).applyMatrix4(o.matrixWorld); out.push(v.clone()); }
    });
    return out;
  }
  function freeZone() {
    var R = ui && ui.root ? ui.root.getBoundingClientRect() : null;
    if (!R || !R.height || !R.width) return { hw: 1 - 2 * MARGIN, yTop: 1 - 2 * MARGIN, yBot: -1 + 2 * MARGIN };
    var H = R.height, top = MARGIN * H, bot = MARGIN * H;
    [ui.tools, ui.help].forEach(function (e) {
      if (!e || e.hidden) return; var r = e.getBoundingClientRect(); if (!r.height || !r.width) return;
      bot = Math.max(bot, R.bottom - r.top + 8);                             /* 8 px au-dessus des commandes */
    });
    var lst = ui.root.querySelector('.is-list');                             /* pastilles rangées en haut (étagère, vue étroite) */
    if (lst) { var rl = lst.getBoundingClientRect(); if (rl.height) top = Math.max(top, rl.bottom - R.top + 8); }
    return { hw: 1 - 2 * MARGIN, yTop: 1 - 2 * top / H, yBot: -1 + 2 * bot / H };
  }
  function fitRadius() {
    var b = view.box; if (!b) return view.R * 3;
    var T = window.THREE, cam = view.camera, r = view.R * 3, cs = [], Z = freeZone(), hh = Math.max(0.15, (Z.yTop - Z.yBot) / 2);
    if (view.fitPts && view.fitPts.length) cs = view.fitPts;
    else [b.min.x, b.max.x].forEach(function (x) { [b.min.y, b.max.y].forEach(function (y) { [b.min.z, b.max.z].forEach(function (z) { cs.push(new T.Vector3(x, y, z)); }); }); });
    if (cam.view && cam.view.enabled) cam.clearViewOffset();
    var t = view.target, x0, x1, y0, y1;
    var measure = function () {
      var sp = Math.sin(view.phi);
      cam.position.set(t.x + r * sp * Math.sin(view.theta), t.y + r * Math.cos(view.phi), t.z + r * sp * Math.cos(view.theta));
      cam.lookAt(t); cam.updateMatrixWorld(); cam.updateProjectionMatrix();
      x0 = y0 = Infinity; x1 = y1 = -Infinity;
      cs.forEach(function (c) { var p = c.clone().project(cam); x0 = Math.min(x0, p.x); x1 = Math.max(x1, p.x); y0 = Math.min(y0, p.y); y1 = Math.max(y1, p.y); });
    };
    for (var k = 0; k < 4; k++) { measure(); r = r * Math.max((x1 - x0) / 2 / Z.hw, (y1 - y0) / 2 / hh); }
    measure();
    view.off = { nx: (x0 + x1) / 2, ny: (Z.yTop + Z.yBot) / 2 - (y0 + y1) / 2 };   /* centre du modèle → centre de la zone libre */
    return r;
  }
  function resetView() {
    if (!view) return; if (view.center) view.target.copy(view.center);
    var pv = view.model && view.model.view;                                   /*  crédence vue de face*/
    view.theta = pv ? pv.theta : VIEW.theta; view.phi = pv ? pv.phi : VIEW.phi;
    view.radius = fitRadius(); view.fitR = view.radius; view.userMoved = false; place();
  }
  function zoom(f) { if (!view) return; moved(); var fr = view.fitR || view.radius; view.radius = Math.max(fr * 0.25, Math.min(fr * 3, view.radius * f)); place(); }
  /* double-clic (ou double appui) sur la pièce : la vue tourne désormais autour de ce point, rapprochée,
     pour voir un coin usiné et son chant de près ; à côté de la pièce : vue d'origine */
  function focusAt(x, y) {
    if (!view || !view.model || !window.THREE) return false;
    var T = window.THREE, r = ui.canvas.getBoundingClientRect(), rc = new T.Raycaster();
    rc.setFromCamera(new T.Vector2((x - r.left) / r.width * 2 - 1, -(y - r.top) / r.height * 2 + 1), view.camera);
    var hit = rc.intersectObject(view.model.group, true).filter(function (h) { return h.object.isMesh; })[0];
    if (!hit) return false;
    moved(); view.target.copy(hit.point);
    var fr = view.fitR || view.radius; view.radius = Math.max(fr * 0.25, Math.min(view.radius, fr * 0.4));
    place(); return true;
  }

  function bindPointer(el) {
    if (el.__cp3) return; el.__cp3 = true;
    var pts = {}, last = null, pinch = 0, tap = null;
    var dist = function () { var k = Object.keys(pts); return k.length < 2 ? 0 : Math.hypot(pts[k[0]].x - pts[k[1]].x, pts[k[0]].y - pts[k[1]].y); };
    var down = null;
    el.addEventListener('pointerdown', function (e) { el.setPointerCapture(e.pointerId); pts[e.pointerId] = { x: e.clientX, y: e.clientY }; last = { x: e.clientX, y: e.clientY }; pinch = dist(); down = { x: e.clientX, y: e.clientY, t: Date.now(), n: Object.keys(pts).length }; });
    el.addEventListener('pointerup', function (e) {                         /* vue d'ensemble (étagère) : clic ou appui court = choisir une pièce ; un glissé ou un pincement, non (A-351) */
      var o = ovr(); if (!o || !down || !o.onPick || down.n > 1) return;
      if (Math.hypot(e.clientX - down.x, e.clientY - down.y) > 8 || Date.now() - down.t > 600) return;
      var id = pickAt(e.clientX, e.clientY); if (id) o.onPick(id);
    });
    el.addEventListener('pointermove', function (e) {
      if (!view || !pts[e.pointerId]) return;
      var of = ovr(); if (of && of.fixed) return;                          /* vue d'ensemble : caméra figée */
      pts[e.pointerId] = { x: e.clientX, y: e.clientY };
      var n = Object.keys(pts).length;
      if (n >= 2) { var dd = dist(); if (pinch > 0 && dd > 0) { var fr = view.fitR || view.radius; view.radius = Math.max(fr * 0.25, Math.min(fr * 3, view.radius * pinch / dd)); place(); } pinch = dd; return; }
      var dx = e.clientX - last.x, dy = e.clientY - last.y; last = { x: e.clientX, y: e.clientY }; moved();
      view.theta -= dx * 0.006; view.phi = Math.max(0.12, Math.min((view.model && view.model.phiMax) || 1.45, view.phi - dy * 0.006)); bornesVue(); place();
    });
    var up = function (e) { delete pts[e.pointerId]; var k = Object.keys(pts); last = k.length ? { x: pts[k[0]].x, y: pts[k[0]].y } : null; pinch = dist(); };
    el.addEventListener('pointerup', up); el.addEventListener('pointercancel', up);
    el.addEventListener('pointerup', function (e) {                         /* double appui au doigt */
      if (e.pointerType !== 'touch') return;
      var of = ovr(); if (of && of.fixed) return;
      var now = Date.now();
      if (tap && now - tap.t < 350 && Math.hypot(e.clientX - tap.x, e.clientY - tap.y) < 24) { tap = null; if (!focusAt(e.clientX, e.clientY)) resetView(); }
      else tap = { t: now, x: e.clientX, y: e.clientY };
    });
    el.addEventListener('wheel', function (e) { if (!view) return; var of = ovr(); if (of && of.fixed) return; e.preventDefault(); zoom(Math.exp(e.deltaY * 0.0012)); }, { passive: false });
    el.addEventListener('dblclick', function (e) {
      var of = ovr(); if (of && of.fixed) return;
      if (e.sourceCapabilities && e.sourceCapabilities.firesTouchEvents) return; /* au doigt : traité ci-dessus */
      if (!focusAt(e.clientX, e.clientY)) resetView();
    });
  }

  /* ── 5. : aperçu de la fenêtre des jambages ──
     Même scène que la vue 3D (buildScene : pièces usinées, décor, chants, jambages à leur retrait), vue fixe
     de trois quarts recadrée à chaque mise à jour. Une toile dans .legs-preview, à la place du croquis du
     moteur (#legs-preview-svg : masqué, toujours tracé, c'est le repli). Three.js : le même chargement que la
     vue 3D, lancé dès le survol du bouton des jambages (preload). Retour : 'ok' (mise à jour), 'loading'
     (première image à venir : onState('ok' | 'nogl' | 'error') ensuite), 'nogl' ou 'error' ; sans 'ok', le
     croquis reste.*/
  var mini = null, miniWant = null;
  function miniCss() {
    if (document.getElementById('cp-l3d-css')) return;
    var st = document.createElement('style'); st.id = 'cp-l3d-css';
    st.textContent =
      '.legs-preview.cp-l3d-on .legs-preview-svg{display:none}' +
      '.cp-l3d{position:relative;flex:1 1 auto;min-width:0;height:260px}' +
      '.cp-l3d canvas{display:block;width:100%;height:100%}';
    document.head.appendChild(st);
  }
  function disposeGroup(g) {
    g.traverse(function (o) { if (o.geometry) o.geometry.dispose(); if (o.material) [].concat(o.material).forEach(function (m) { m.dispose(); }); });
  }
  function miniStop() {
    miniWant = null;
    if (!mini) return;
    var m = mini; mini = null;
    window.removeEventListener('resize', m.onResize); if (m.ro) m.ro.disconnect();
    if (m.model) disposeGroup(m.model.group);
    m.dispose.forEach(function (x) { try { x.dispose(); } catch (e) {} });
    if (m.tex) m.tex.dispose();
    try { m.renderer.dispose(); m.renderer.forceContextLoss(); } catch (e) {}
    if (m.box.parentNode) m.box.parentNode.removeChild(m.box);
    m.host.classList.remove('cp-l3d-on');
  }
  function miniDraw() {
    if (!mini || !mini.bbox) return;
    var T = window.THREE, r = mini.box.getBoundingClientRect(), w = Math.round(r.width), h = Math.round(r.height);
    if (w < 2 || h < 2) return;                                             /* fenêtre pas encore affichée */
    var cam = mini.camera, t = mini.center, th = -0.55, ph = 1.0, b = mini.bbox, cs = [], rad = mini.R * 3;
    if (w !== mini.w || h !== mini.h) { mini.renderer.setSize(w, h, false); cam.aspect = w / h; cam.updateProjectionMatrix(); mini.w = w; mini.h = h; }
    [b.min.x, b.max.x].forEach(function (x) { [b.min.y, b.max.y].forEach(function (y) { [b.min.z, b.max.z].forEach(function (z) { cs.push(new T.Vector3(x, y, z)); }); }); });
    for (var k = 0; k < 3; k++) {                                           /* la boîte tient dans 90 % de la toile */
      cam.position.set(t.x + rad * Math.sin(ph) * Math.sin(th), t.y + rad * Math.cos(ph), t.z + rad * Math.sin(ph) * Math.cos(th));
      cam.lookAt(t); cam.updateMatrixWorld();
      var mx = 0; cs.forEach(function (c) { var p = c.clone().project(cam); mx = Math.max(mx, Math.abs(p.x), Math.abs(p.y)); });
      rad = rad * mx / 0.9;
    }
    cam.position.set(t.x + rad * Math.sin(ph) * Math.sin(th), t.y + rad * Math.cos(ph), t.z + rad * Math.sin(ph) * Math.cos(th));
    cam.lookAt(t);
    mini.renderer.render(mini.scene, cam);
  }
  function miniBuild(plan) {
    var T = window.THREE;
    if (mini.model) { mini.scene.remove(mini.model.group); disposeGroup(mini.model.group); }
    var m = buildScene(T, plan, mini.tex); mini.model = m; mini.scene.add(m.group);
    var box = new T.Box3().setFromObject(m.group), size = box.getSize(new T.Vector3()), c = box.getCenter(new T.Vector3());
    mini.bbox = box; mini.center = c; mini.R = Math.max(size.length() / 2, 300);
    mini.floor.scale.set(size.x * 3 + 4000, size.z * 3 + 4000, 1); mini.floor.position.set(c.x, m.legH ? -m.legH - 1 : -60, c.z);
    mini.floor.material.opacity = m.wall ? 0 : 0.22;                    /* R140 : étagère, pas d'ombre au sol (comme la vue) */
    mini.sun.position.set(c.x - mini.R * 1.1, c.y + mini.R * 1.6, c.z + mini.R * 1.3); mini.sun.target.position.copy(c);
    mini.fill.position.set(c.x + mini.R * 1.5, c.y + mini.R * 0.8, c.z - mini.R * 1.2); mini.fill.target.position.copy(c);
    placeFenetre(mini.win, c, mini.R, m.wall);
    var sc = mini.sun.shadow.camera, e = mini.R * 1.6; sc.left = -e; sc.right = e; sc.top = e; sc.bottom = -e; sc.near = 10; sc.far = mini.R * 8; sc.updateProjectionMatrix();
    miniDraw();
  }
  function legsPreview(host, plan, onState) {
    if (!host || !plan) return 'error';
    if (!hasWebGL()) { miniStop(); return 'nogl'; }
    if (mini && mini.host === host) {
      try { miniUpdate(plan); return 'ok'; } catch (e) { miniStop(); console.warn('[cp-3d]', e); return 'error'; }
    }
    if (mini) miniStop();
    /* première image : après l'affichage de la fenêtre (le croquis du moteur s'y voit d'abord), pour que le
       clic ne soit pas bloqué par le chargement de Three.js ni par la préparation des ombrages */
    var first = !miniWant;
    miniWant = { host: host, plan: plan, onState: onState };
    if (first) loadThree().then(function () {
      return new Promise(function (r) { requestAnimationFrame(function () { setTimeout(r, 0); }); });
    }).then(function () {
      var w = miniWant; miniWant = null;
      if (!w || !w.host.isConnected || w.host.closest('.hidden')) return;    /* fenêtre fermée entre-temps */
      var s;
      try { s = miniStart(w.host, w.plan); } catch (e) { miniStop(); console.warn('[cp-3d]', e); s = 'error'; }
      if (w.onState) w.onState(s);
    }, function () {                                                          /* Three.js injoignable */
      var w = miniWant; miniWant = null;
      if (w && w.onState) w.onState('error');
    });
    return 'loading';
  }
  function miniStart(host, plan) {
    var T = window.THREE;
    miniCss();
    var box = document.createElement('div'), cv = document.createElement('canvas'), renderer;
    box.className = 'cp-l3d';
    cv.setAttribute('role', 'img'); cv.setAttribute('aria-label', 'Aperçu en 3D du plan et de ses jambages');
    if (document.getElementById('cp-l3d-cap')) cv.setAttribute('aria-describedby', 'cp-l3d-cap');
    box.appendChild(cv);
    var svg = host.querySelector('.legs-preview-svg'); host.insertBefore(box, svg ? svg.nextSibling : null);
    try { renderer = new T.WebGLRenderer({ canvas: cv, antialias: true, alpha: true }); }
    catch (e) { box.parentNode.removeChild(box); glOk = false; return 'nogl'; }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputEncoding = T.sRGBEncoding;
    renderer.shadowMap.enabled = true; renderer.shadowMap.type = T.PCFSoftShadowMap;
    renderer.setClearColor(0x000000, 0);                                    /* fond du cadre de la fenêtre */
    var scene = new T.Scene(), camera = new T.PerspectiveCamera(35, 1, 10, 100000);
    var Lm = lumieres(T, scene), sun = Lm.sun, fill = Lm.fill;              /* mêmes lumières que la vue 3D */
    var fg = new T.PlaneGeometry(1, 1), floor = new T.Mesh(fg, new T.ShadowMaterial({ opacity: 0.22 }));
    floor.rotation.x = -Math.PI / 2; floor.receiveShadow = true; scene.add(floor);
    mini = { host: host, box: box, renderer: renderer, scene: scene, camera: camera, sun: sun, fill: fill, win: Lm.win, floor: floor, dispose: [fg, floor.material], tex: null, texUrl: null, w: 0, h: 0 };
    host.classList.add('cp-l3d-on');
    mini.onResize = miniDraw; window.addEventListener('resize', miniDraw);
    if (window.ResizeObserver) { mini.ro = new ResizeObserver(function () { miniDraw(); }); mini.ro.observe(box); }
    miniUpdate(plan);
    return 'ok';
  }
  function miniUpdate(plan) {
    var T = window.THREE;
    mini.plan = plan;
    var A = getApp(), url = A && A.getWorkImageUrl ? A.getWorkImageUrl() : null;
    if (url && url !== mini.texUrl) {
      var mm = mini, loader = new T.TextureLoader(); mm.texUrl = url; loader.setCrossOrigin('anonymous');
      loader.load(url, function (tx) {
        if (mini !== mm || mm.texUrl !== url) { tx.dispose(); return; }
        tx.wrapS = tx.wrapT = T.MirroredRepeatWrapping; tx.flipY = false; tx.encoding = T.sRGBEncoding;
        tx.anisotropy = Math.min(8, mm.renderer.capabilities.getMaxAnisotropy ? mm.renderer.capabilities.getMaxAnisotropy() : 1);
        if (mm.tex) mm.tex.dispose();
        mm.tex = tx; miniBuild(mm.plan);
      }, undefined, function () {});
    }
    miniBuild(plan);
  }
  function preload() { if (hasWebGL()) loadThree().catch(function () {}); }

  /* ── ──
     PNG de l'angle de vue courant, rendu à la demande en haute définition (≈ 1 800 px de large) puis copié DANS LA
     MÊME TÂCHE (le tampon WebGL n'est pas conservé), avec un bandeau : décor, dimensions, épaisseur, creapanel.com.
     Nom : creapanel-<décor>-<L>x<P>.png (forme droite) ou creapanel-<décor>-<forme>-<longueur>mm.png. Tout est
     synchrone (toDataURL) : sur mobile, le partage de fichier reste dans le geste du client (iOS) ; sinon, vrai
     téléchargement par un lien « download ».*/
  function slug(s) { return String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }
  function shotInfo() {
    var A = getApp(), S = (A && A.S) || {}, d = (typeof window.CP_decorFull === 'function') ? window.CP_decorFull() : null;
    var decor = (d && d.name) || (A && A.variantsMeta && A.variantsMeta[S.variant] && A.variantsMeta[S.variant].name) || 'Décor';
    var forme = typeof window.CP_formeName === 'function' ? window.CP_formeName(S) : '', dims, file;
    var et = typeof window.CP_etagModel === 'function' ? window.CP_etagModel() : null;   /* étagère V2 : toutes les étagères */
    /*  milliers séparés comme sur le reste du site (« 1 200 », espace insécable) ; le nom du fichier garde les chiffres seuls*/
    var fr = function (v) { return String(Math.round(num(v))).replace(/\B(?=(\d{3})+(?!\d))/g, ' '); };
    /* A-402 (UX ; AUDIT Tour 935 ; forme de COMMERCIAL, Tour 171) : boiseries, puis étagères, puis montants, chacun compté à
       part, dans l'ordre des numéros : « 1 boiserie : 1 800 × 1 200 mm · 1 étagère : 2 324 mm · 2 montants : 2 500 / 2 500 mm » ;
       un groupe vide n'est pas écrit. Les montants passaient pour des étagères, et les panneaux n'étaient pas cités */
    var bois = typeof window.CP_boisModel === 'function' ? window.CP_boisModel() : null, bp = (bois && bois.panneaux) || [];
    if (et && (et.nombre > 0 || bp.length)) {
      var eh = et.liste.filter(function (s) { return s.orientation !== 'verticale'; }), ev = et.liste.filter(function (s) { return s.orientation === 'verticale'; });
      var grp = function (n, un, plus, vals) { return n ? n + ' ' + (n > 1 ? plus : un) + ' : ' + vals.join(' / ') + ' mm' : ''; };
      /* R160 (COMMERCIAL, Tour 173 § 5) : à profondeurs différentes (le modèle donne alors profondeur_mm à chaque pièce),
         « 800 × 260 » par étagère et par montant ; les boiseries gardent largeur × hauteur */
      var lp = function (s) { return fr(s.longueur_mm) + (s.profondeur_mm != null ? ' × ' + fr(s.profondeur_mm) : ''); };
      dims = [grp(bp.length, 'boiserie', 'boiseries', bp.map(function (p) { return fr(p.largeur_mm) + ' × ' + fr(p.hauteur_mm); })),
        grp(eh.length, 'étagère', 'étagères', eh.map(lp)), grp(ev.length, 'montant', 'montants', ev.map(lp))].filter(Boolean).join(' · ');
      var fn = function (n, un, plus) { return n ? n + '-' + (n > 1 ? plus : un) : ''; };
      file = [fn(bp.length, 'boiserie', 'boiseries'), fn(eh.length, 'etagere', 'etageres'), fn(ev.length, 'montant', 'montants')].filter(Boolean).join('-') + '-' + num(S.P);
    }
    else if (S.layout === 'linear') { dims = fr(S.L) + ' × ' + fr(S.P) + ' mm'; file = num(S.L) + 'x' + num(S.P); }
    else {
      var mm = A && typeof A.getTotalLengthMm === 'function' ? Math.round(num(A.getTotalLengthMm())) : 0;
      dims = (forme ? forme + ', ' : '') + (mm / 1000).toFixed(2).replace('.', ',') + ' ml';
      file = slug(forme || S.layout) + '-' + mm + 'mm';
    }
    /* boiserie : pas d'épaisseur unique (panneaux 18 mm, étagères 38 mm) */
    return { decor: decor, dims: dims, thk: bp.length ? 0 : num(S.thk), name: 'creapanel-' + slug(decor) + '-' + file + '.png' };
  }
  function snapshot() {
    if (!view || !view.model || !window.THREE) return null;
    var T = window.THREE, R = view.renderer, cv = R.domElement, pr0 = R.getPixelRatio(), s = R.getSize(new T.Vector2());
    /* R39-d (AUDIT Tour 1123, d'après UX) : l'image est toujours le plan entier recentré (le cadrage de « Recentrer »), quel que
       soit le zoom ou l'angle à l'écran ; la vue du client est remise telle quelle juste après */
    var sv = { t: view.target.clone(), th: view.theta, ph: view.phi, r: view.radius, fr: view.fitR, um: view.userMoved, off: view.off ? { nx: view.off.nx, ny: view.off.ny } : null };
    if (view.center) view.target.copy(view.center);
    var pv = view.model && view.model.view; view.theta = pv ? pv.theta : VIEW.theta; view.phi = pv ? pv.phi : VIEW.phi;
    view.radius = fitRadius(); place();
    var scale = Math.max(1, Math.min(3, 1800 / Math.max(1, s.x)));
    R.setPixelRatio(scale); R.setSize(s.x, s.y, false); applyOffset();
    lueur(view.model.group, true);                                             /* image sans la surbrillance */
    R.render(view.scene, view.camera);
    lueur(view.model.group);
    var W = cv.width, H = cv.height, band = Math.round(Math.max(48, H * 0.075));
    var info = shotInfo(), fs = Math.round(band * 0.36), pad = Math.round(band * 0.45), FONT = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
    /* Légende trop longue pour une ligne (téléphone, plusieurs étagères : « 38 mm » passait sous « creapanel.com ») :
       police réduite jusqu'à 80 % ; au-delà, creapanel.com passe sur une 2e ligne et la légende prend toute la largeur
       (police jusqu'à 60 %, puis coupée par « … »). */
    var txt = info.decor + '  ·  ' + info.dims + (info.thk ? '  ·  ' + info.thk + ' mm' : ''), fl = fs, two = false;
    var mg = document.createElement('canvas').getContext('2d');
    mg.font = '500 ' + fs + 'px ' + FONT; var wr = mg.measureText('creapanel.com').width;
    var wl = function (f) { mg.font = '600 ' + f + 'px ' + FONT; return mg.measureText(txt).width; };
    var room = W - 3 * pad - wr;
    if (wl(fs) > room) {
      fl = Math.floor(fs * room / wl(fs));
      if (fl < fs * 0.8) { two = true; fl = Math.max(Math.round(fs * 0.6), Math.min(fs, Math.floor(fs * (W - 2 * pad) / wl(fs)))); }
      while (txt.length > 4 && wl(fl) > (two ? W - 2 * pad : room)) txt = txt.slice(0, -2) + '…';
    }
    var bh = two ? Math.round(band * 1.7) : band;
    var out = document.createElement('canvas'); out.width = W; out.height = H + bh;
    var g = out.getContext('2d');
    g.fillStyle = '#f1ede5'; g.fillRect(0, 0, W, H);
    g.drawImage(cv, 0, 0);                                                     /* même tâche que le rendu */
    view.target.copy(sv.t); view.theta = sv.th; view.phi = sv.ph; view.radius = sv.r; view.fitR = sv.fr; view.userMoved = sv.um; view.off = sv.off; place();   /* la vue du client */
    R.setPixelRatio(pr0); R.setSize(s.x, s.y, false); applyOffset(); requestRender();
    g.fillStyle = '#0d3527'; g.fillRect(0, H, W, bh);
    g.textBaseline = 'middle'; g.fillStyle = '#ffffff';
    g.font = '600 ' + fl + 'px ' + FONT; g.textAlign = 'left';
    g.fillText(txt, pad, H + (two ? bh * 0.34 : bh / 2));
    g.font = '500 ' + fs + 'px ' + FONT; g.textAlign = two ? 'left' : 'right';
    g.fillText('creapanel.com', two ? pad : W - pad, H + (two ? bh * 0.7 : bh / 2));
    return { url: out.toDataURL('image/png'), name: info.name, w: W, h: H + bh };
  }
  function downloadImage() {
    var sh = snapshot(); if (!sh) return;
    var bin = atob(sh.url.split(',')[1]), buf = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
    var blob = new Blob([buf], { type: 'image/png' }), file = null;
    try { file = new File([blob], sh.name, { type: 'image/png' }); } catch (e) {}
    var touch = !!(window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches);
    var viaLink = function () {
      var u = URL.createObjectURL(blob), a = document.createElement('a');
      a.href = u; a.download = sh.name; a.rel = 'noopener'; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(function () { URL.revokeObjectURL(u); }, 5000);
    };
    if (touch && file && navigator.canShare && navigator.canShare({ files: [file] })) {
      navigator.share({ files: [file], title: 'Mon plan Creapanel' }).catch(function (e) { if (!e || e.name !== 'AbortError') viaLink(); });
      return;
    }
    viaLink();
  }

  /* ── Vue 3D sur la facture ──
     À l'ajout au panier, propriété cachée _preview_3d_url : JPEG de la vue 3D par défaut du produit (même angle que
     le bouton 3D), fond blanc, rendue HORS ÉCRAN (moteur WebGL à part, la vue affichée n'est pas touchée), 3D ouverte
     ou non. Même mécanique que _preview_image_url (configurator-main, dégradation par paliers) : les DEUX images
     ensemble sous 55 000 caractères, la limite qui marche aujourd'hui ; le plan d'abord, la 3D dans ce qui reste,
     sinon _preview_3d_skipped (raison). Sans WebGL ou Three.js : pas d'image, sans erreur. Propriété « _ » : masquée
     au panier, sur la commande et dans les e-mails (custom-panier, main-order : clés « _ » exclues).*/
  var INV_TOTAL = 55000, INV_RATIO = 1.5, INV_STEPS = [{ w: 720, q: 0.5 }, { w: 600, q: 0.42 }, { w: 480, q: 0.36 }, { w: 380, q: 0.3 }, { w: 300, q: 0.26 }];
  function invoiceRender(T, plan, tex, budget) {
    var cv = document.createElement('canvas'), R;
    try { R = new T.WebGLRenderer({ canvas: cv, antialias: true, preserveDrawingBuffer: true }); } catch (e) { return { skip: 'sans WebGL' }; }
    var m = null;
    try {
      R.setPixelRatio(1); R.outputEncoding = T.sRGBEncoding; R.shadowMap.enabled = true; R.shadowMap.type = T.PCFSoftShadowMap; R.setClearColor(0xffffff, 1);
      var scene = new T.Scene(), cam = new T.PerspectiveCamera(35, INV_RATIO, 10, 100000);
      var Li = lumieres(T, scene), sun = Li.sun, fill = Li.fill;                 /* mêmes lumières que la vue 3D */
      m = buildScene(T, plan, tex); scene.add(m.group);
      if (m.sol) m.sol.visible = false;                                          /* A-351 : la facture, sans le sol de la vue */
      var box = new T.Box3().setFromObject(m.fit || m.group), size = box.getSize(new T.Vector3()), c = box.getCenter(new T.Vector3()), RR = Math.max(size.length() / 2, 300);
      if (!m.wall) {                                                             /* ombre au sol discrète (sauf étagère) */
        var floor = new T.Mesh(new T.PlaneGeometry(1, 1), new T.ShadowMaterial({ opacity: m.vertical ? 0.1 : 0.22 }));
        floor.rotation.x = -Math.PI / 2; floor.receiveShadow = true; floor.scale.set(size.x * 3 + 4000, size.z * 3 + 4000, 1);
        floor.position.set(c.x, m.vertical ? -1 : m.legH ? -m.legH - 1 : -60, c.z); scene.add(floor);
      }
      sun.position.set(c.x - RR * 1.1, c.y + RR * 1.6, c.z + RR * 1.3); sun.target.position.copy(c);
      if (m.wall) sun.position.set(c.x - RR * 0.25, c.y + RR * 0.6, c.z + RR * 2.4);   /* É-1 : ombres de contact courtes */
      fill.position.set(c.x + RR * 1.5, c.y + RR * 0.8, c.z - RR * 1.2); fill.target.position.copy(c);
      placeFenetre(Li.win, c, RR, m.wall);
      var sc = sun.shadow.camera, e = RR * 1.6; sc.left = -e; sc.right = e; sc.top = e; sc.bottom = -e; sc.near = 10; sc.far = RR * 8; sc.updateProjectionMatrix();
      /* cadrage : le modèle tient dans 90 % de l'image, à l'angle par défaut du produit */
      var v = m.view || VIEW, th = v.theta, ph = v.phi, pts = modelPoints(T, m.fit || m.group), rad = RR * 3;
      var W0 = INV_STEPS[0].w, H0 = Math.round(W0 / INV_RATIO);
      R.setSize(W0, H0, false); cam.aspect = W0 / H0; cam.updateProjectionMatrix();
      for (var k = 0; k < 4; k++) {
        cam.position.set(c.x + rad * Math.sin(ph) * Math.sin(th), c.y + rad * Math.cos(ph), c.z + rad * Math.sin(ph) * Math.cos(th));
        cam.lookAt(c); cam.updateMatrixWorld();
        var mx = 0; pts.forEach(function (q) { var p = q.clone().project(cam); mx = Math.max(mx, Math.abs(p.x), Math.abs(p.y)); });
        if (mx > 0) rad = rad * mx / 0.9;
      }
      cam.position.set(c.x + rad * Math.sin(ph) * Math.sin(th), c.y + rad * Math.cos(ph), c.z + rad * Math.sin(ph) * Math.cos(th)); cam.lookAt(c);
      R.render(scene, cam);
      var last = 0;
      for (var i = 0; i < INV_STEPS.length; i++) {
        var st = INV_STEPS[i], o = document.createElement('canvas'); o.width = st.w; o.height = Math.round(st.w / INV_RATIO);
        var g = o.getContext('2d'); g.fillStyle = '#ffffff'; g.fillRect(0, 0, o.width, o.height); g.drawImage(cv, 0, 0, o.width, o.height);
        var u = o.toDataURL('image/jpeg', st.q); last = u.length;
        if (u.length <= budget) return { url: u, meta: st.w + 'px q' + st.q + ' · ' + u.length + 'c (plan + 3D ≤ ' + INV_TOTAL + ')' };
      }
      return { skip: 'trop volumineux (' + last + ' caractères pour ' + budget + ' restants)' };
    } finally {
      if (m) disposeGroup(m.group);
      try { R.dispose(); R.forceContextLoss(); } catch (e) {}
    }
  }
  function invoice3d(budget) {
    return new Promise(function (resolve) {
      if (!hasWebGL()) return resolve({ skip: 'sans WebGL' });
      if (typeof window.CP_plan !== 'function') return resolve({ skip: 'pas de modèle' });
      var done = false, fin = function (r) { if (!done) { done = true; clearTimeout(to); resolve(r); } };
      var to = setTimeout(function () { fin({ skip: 'délai dépassé' }); }, 8000);
      loadThree().then(function () {
        var T = window.THREE, plan = window.CP_plan(), A = getApp(), url = A && A.getWorkImageUrl ? A.getWorkImageUrl() : null;
        var go = function (tex) {
          if (done) { if (tex) tex.dispose(); return; }
          var r; try { r = invoiceRender(T, plan, tex, budget); } catch (e) { r = { skip: 'erreur de rendu' }; } finally { if (tex) tex.dispose(); }
          fin(r);
        };
        var suite = function () {                                 /* R132 : images des pièces au décor propre d'abord (étagère) */
          if (!url) return go(null);
          var ld = new T.TextureLoader(); ld.setCrossOrigin('anonymous');
          ld.load(url, function (tx) { tx.wrapS = tx.wrapT = T.MirroredRepeatWrapping; tx.flipY = false; tx.encoding = T.sRGBEncoding; go(tx); }, undefined, function () { go(null); });
        };
        /* images des pièces attendues 4 s au plus (le tout a 8 s) : au-delà, l'image part avec ce qui est là (aplat) */
        Promise.race([texPieces(T), new Promise(function (r) { setTimeout(r, 4000); })]).then(suite, suite);
      }, function () { fin({ skip: 'Three.js injoignable' }); });
    });
  }
  (function hookInvoice() {
    var tries = 0, t = setInterval(function () {
      var A = getApp();
      if (A && typeof A.buildLineItemProperties === 'function' && !A.__cp3dInvoice) {
        clearInterval(t); A.__cp3dInvoice = true;
        var oP = A.buildLineItemProperties;
        A.buildLineItemProperties = async function () {
          var p = await oP.apply(this, arguments);
          try {
            if (p && typeof p === 'object') {
              var used = String(p['_preview_image_url'] || '').length, budget = INV_TOTAL - used, r;
              r = budget >= 4000 ? await invoice3d(budget) : { skip: 'plan prioritaire (' + used + ' caractères)' };
              if (r && r.url) { p['_preview_3d_url'] = r.url; p['_preview_3d_meta'] = r.meta; }
              else p['_preview_3d_skipped'] = (r && r.skip) || 'inconnu';
            }
          } catch (e) { if (p) p['_preview_3d_skipped'] = 'erreur'; }
          return p;
        };
      } else if (++tries > 600) clearInterval(t);
    }, 100);
  })();

  window.CP_3d = { open: open, close: close, machine: machine, legFootprint: legFootprint, redraw: function () { requestRender(); }, root: function () { return ui && ui.root; },
    refit: function () { if (view && view.box && !view.userMoved) { view.radius = fitRadius(); view.fitR = view.radius; place(); } }, project: function (x, y, z) { return view && window.THREE ? project(x, y, z) : null; },
    legsPreview: legsPreview, legsPreviewStop: miniStop, preload: preload, is3d: function () { return on; }, highlight: highlight,
    image: snapshot, downloadImage: downloadImage,
    info: function () { return { on: on, pret: !!(view && view.model), reconstructions: view ? view.rebuilds : 0, vue: view ? { theta: +view.theta.toFixed(2), phi: +view.phi.toFixed(2) } : null, cadre: cadre(), surbrillance: hlId,
      sol: view && view.model && view.model.sol ? view.model.sol.visible : null,   /* A-351 : sol affiché (étagère) */
      trous: view && view.scene ? (function () { var n = 0; view.scene.traverse(function (o) { n += (o.userData && o.userData.trous) || 0; }); return n; })() : null,   /* R200 : trous percés dans la scène */
      /* R39-c : cuves creusées sous leur trou (évier intégré) : une par évier, { fond, profondeur_mm, egouttoir, bonde_mm } */
      cuves: view && view.scene ? (function () { var l = []; view.scene.traverse(function (o) { var c = o.userData && o.userData.cuve; if (c && typeof c === 'object') l.push(c); }); return l; })() : null,
      monobloc: view && view.scene ? monoInfo() : null,
      /* évier intégré : profondeurs sous le dessus où une verticale touche la cuve, au quart de l'intérieur et en son centre
         (bonde) : la cuve n'est pas traversante si la verticale du quart touche un fond */
      cuvesVerticales: view && view.scene ? (function () {
        var ms = [], ps = []; view.scene.traverse(function (o) { if (o.isMesh && o.userData && o.userData.cuve) ms.push(o); if (o.userData && o.userData.cuveInt) ps.push(o.userData); });
        var r1 = function (v) { return Math.round(v * 10) / 10; };
        return ps.map(function (u) {
          var q = u.cuveInt, x0 = q[0][0], z0 = q[0][1], x1 = q[2][0], z1 = q[2][1];
          var v = function (x, z) { var ys = []; ms.forEach(function (m) { verticale(m.geometry, x, z).forEach(function (y) { ys.push(r1(u.cuveTH - y)); }); }); return ys.filter(function (y, k) { return ys.indexOf(y) === k; }).sort(function (a, b) { return a - b; }); };
          return { quart: v(x0 + (x1 - x0) / 4, z0 + (z1 - z0) / 4), centre: v((x0 + x1) / 2, (z0 + z1) / 2) };
        });
      })() : null }; } };
  /* R39-b : cotes du monobloc relues dans la scène (repère du plan, mm) : boîte de sa géométrie, dessus par rapport au plan ;
     verticales (profondeurs sous le dessus où elles touchent une face) au quart de la cuve, au centre de la bonde, au trou de
     robinet et dans la matière : la cuve n'est pas traversante, la bonde et le robinet le sont */
  function monoInfo() {
    var m = null; view.scene.traverse(function (o) { if (!m && o.userData && o.userData.monobloc) m = o; }); if (!m) return null;
    var u = m.userData.monobloc, g = u.geo, gb = m.geometry; gb.computeBoundingBox();
    var b = gb.boundingBox, r1 = function (v) { return Math.round(v * 10) / 10; };
    var vert = function (uu, vv) { return verticale(gb, u.x_mm + uu, vv).map(function (y) { return r1(u.dessus_y - y); }); };
    return { code: u.code, x_mm: r1(b.min.x), longueur_mm: r1(b.max.x - b.min.x), profondeur_mm: r1(b.max.z - b.min.z), hauteur_mm: r1(b.max.y - b.min.y),
      dessus_au_niveau_du_plan: Math.abs(b.max.y - u.dessus_y) < 0.05, triangles: gb.getAttribute('position').count / 3,
      verticales: { cuve: vert(g.cuve.u + g.cuve.l / 4, g.cuve.v + g.cuve.p / 4), bonde: vert(g.bonde.u, g.bonde.v), robinet: vert(g.robinet.u, g.robinet.v), matiere: vert(g.cuve.u / 2, g.p / 2) } };
  }
  /* contrôles : pente à l'écran de l'arête avant haute de la boîte du modèle, en degrés (positive = descend de
     gauche à droite), et largeur occupée à l'écran (part de la largeur du cadre)*/
  function cadre() {
    if (!view || !view.box || !window.THREE) return null;
    var T = window.THREE, b = view.box, cam = view.camera, pr = function (x, y, z) { return new T.Vector3(x, y, z).project(cam); };
    var l = pr(b.min.x, b.max.y, b.max.z), r = pr(b.max.x, b.max.y, b.max.z), xs = [];
    [b.min.x, b.max.x].forEach(function (x) { [b.min.y, b.max.y].forEach(function (y) { [b.min.z, b.max.z].forEach(function (z) { xs.push(pr(x, y, z).x); }); }); });
    var asp = cam.aspect || 1, ang = Math.atan2(-(r.y - l.y), (r.x - l.x) * asp) * 180 / Math.PI;
    /*  hauteur occupée, et marges en px entre le modèle et les bords du cadre / le haut des commandes*/
    var ys = [];
    if (view.fitPts && view.fitPts.length) { xs = []; view.fitPts.forEach(function (q) { var p = q.clone().project(cam); xs.push(p.x); ys.push(p.y); }); }
    else [b.min.x, b.max.x].forEach(function (x) { [b.min.y, b.max.y].forEach(function (y) { [b.min.z, b.max.z].forEach(function (z) { ys.push(pr(x, y, z).y); }); }); });
    var R = ui.root.getBoundingClientRect(), W = R.width, H = R.height, ymin = Math.min.apply(null, ys), ymax = Math.max.apply(null, ys), xmin = Math.min.apply(null, xs), xmax = Math.max.apply(null, xs);
    var bottomPx = (1 - ymin) / 2 * H, topPx = (1 - ymax) / 2 * H, ctl = Infinity;
    [ui.tools, ui.help].forEach(function (e) { if (e && !e.hidden) { var q = e.getBoundingClientRect(); if (q.height) ctl = Math.min(ctl, q.top - R.top); } });
    return { pente_deg: +ang.toFixed(1), largeur: +((xmax - xmin) / 2).toFixed(2), hauteur: +((ymax - ymin) / 2).toFixed(2),
      marge_gauche_px: Math.round((xmin + 1) / 2 * W), marge_droite_px: Math.round((1 - xmax) / 2 * W), marge_haut_px: Math.round(topPx),
      jeu_commandes_px: isFinite(ctl) ? Math.round(ctl - bottomPx) : null };
  }

  var tries = 0, timer = setInterval(function () {
    if (ensureButton() || ++tries > 200) clearInterval(timer);
  }, 100);
  /*  la 3D suit chaque rendu du moteur ; une seule toile WebGL : la fenêtre des jambages
     ramène en 2D le temps qu'elle est ouverte, puis la 3D revient*/
  var wTries = 0, wTimer = setInterval(function () {
    var A = getApp();
    if (A && typeof A.render === 'function' && A.svg) {
      clearInterval(wTimer);
      if (!A.__cp3dWrapped) { A.__cp3dWrapped = true; var o = A.render; A.render = function () { var r = o.apply(this, arguments); refreshSoon(); return r; }; }
      var pop = document.getElementById('legs-popup');
      if (pop && window.MutationObserver) new MutationObserver(function () {
        var open2 = !pop.classList.contains('hidden');
        if (open2 && on) { back3d = true; exit(true); }
        else if (!open2 && back3d) { back3d = false; enter(); }
      }).observe(pop, { attributes: true, attributeFilter: ['class'] });
    } else if (++wTries > 400) clearInterval(wTimer);
  }, 100);
  /* Three.js (118 Ko) : au geste (survol, focus, toucher de l'interrupteur) ou au repos du navigateur après le
     chargement complet de la page, jamais avant l'affichage*/
  window.addEventListener('load', function () {
    var idle = window.requestIdleCallback || function (f) { return setTimeout(f, 2000); };
    idle(function () { preload(); }, { timeout: 6000 });
  });
  document.addEventListener('pointerover', function (e) { if (e.target && e.target.closest && e.target.closest('#cp-3d-btn')) preload(); }, { passive: true });
  document.addEventListener('focusin', function (e) { if (e.target && e.target.id === 'cp-3d-btn') preload(); });
})();
